import React, { Component, Suspense } from 'react';
import clsx from 'clsx';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { CssBaseline, Container, Typography, Box, Button, Stepper, Step, StepLabel, TextField, MenuItem, Chip, Radio, RadioGroup, FormControlLabel, Switch, Divider, CircularProgress, Alert, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TablePagination, ToggleButton, ToggleButtonGroup, Skeleton } from '@mui/material';
import { withStyles } from '@mui/styles';
import { Styles } from '../../styles/material-styles';
import HeaderMenu from '../../components/header-menu';
import Footer from '../../components/footer';
import { injectIntl } from 'react-intl';
import * as componentActions from '../../redux/actions/header-menu';
import CustomizedSnackbars from '../../components/material-snackbars';
import SimpleAutoComplete from '../../components/auto-complete/autocomplete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Check from '@mui/icons-material/Check';
import BoltIcon from '@mui/icons-material/Bolt';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import RepeatIcon from '@mui/icons-material/Repeat';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Api from '../../services/api';
import MarketingService, { normalizeBroadcastDetail, normalizeList, normalizeSmsSender, normalizeEmailSender, normalizeEmailSenderRow } from '../../services/marketing.service';
import { GetAllActiveAndByMessageProvider, getPhonesAssociations } from '../../services/2talk/channel';
import { getApprovedByRegistrationNumberRequestId, getApprovedOptInByRegistrationNumberRequestId } from '../../services/twotalk.template.message.service';
import { Overlay } from '../../styles/global';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { list as listEmailTemplates, renderForMarketing as renderEmailTemplateForMarketing, uploadMarketingAttachment, getAssetUrls } from '../../services/emailtemplate.service';

// Editores reaproveitados da Automação (lazy — bundles pesados só carregam no passo de e-mail):
// Jodit = WYSIWYG em iframe isolado (renderiza o template com CSS, modo documento completo);
// Monaco = edição de HTML cru (avançado). Mesmo padrão do bloco de ação de e-mail da Automação.
const EmailAssetRichEditor = React.lazy(() => import('../../components/emailConfig/EmailAssetRichEditor'));
const CodeEditor = React.lazy(() => import('../../components/code-editor/CodeEditor'));

const WHATSAPP_PROVIDER_ID = 2; // canal WhatsApp (tb_2talk_message_provider)

// Shape real do template (mesmos campos da automação de envio via provedor). Categoria/idioma vêm da API
// (sem valor chumbado); as variáveis {{n}} são derivadas do corpo.
const normalizeTemplate = (t = {}) => {
  const header = t.headertext ?? t.headerText ?? '';
  const body = t.bodytext ?? t.bodyText ?? t.body ?? '';
  const footer = t.footertext ?? t.footerText ?? '';
  const vars = Array.from(new Set((String(body).match(/\{\{\s*\d+\s*\}\}/g) || [])));
  return {
    id: t.id ?? t.Id,
    name: t.name ?? t.Name ?? '',
    language: t.language ?? t.templatelanguage ?? t.templateLanguage ?? '',
    category: t.category ?? t.templatecategory ?? t.templateCategory ?? '',
    header: String(header),
    body: String(body),
    footer: String(footer),
    system: t.system ?? t.System ?? false,   // optin: o default do número é o system=true
    vars,
  };
};

const normalizeChannel = (c = {}) => ({ id: c.id, name: c.name ?? c.channelname ?? `#${c.id}` });

// Serviços externos podem devolver array cru OU o corpo do CustomResponse ({ data: [...] }).
const asArray = (raw) => (Array.isArray(raw) ? raw : (raw && Array.isArray(raw.data) ? raw.data : []));

// Ícone de step customizado: ativo ganha anel/glow + leve zoom; concluído vira check verde;
// futuro fica apagado. Dá um destaque claro de "onde estou" no wizard.
function StepCircle(props) {
  const { active, completed, icon } = props;
  const base = {
    width: 34, height: 34, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 14, fontWeight: 700, transition: 'all .25s ease',
  };
  if (completed) return <Box sx={{ ...base, backgroundColor: '#5b9b1e', color: '#fff' }}><Check sx={{ fontSize: 18 }} /></Box>;
  if (active) return (
    <Box sx={{ ...base, backgroundColor: '#8BC34A', color: '#fff', transform: 'scale(1.18)', boxShadow: '0 0 0 5px rgba(139,195,74,0.25), 0 4px 10px rgba(139,195,74,0.45)' }}>{icon}</Box>
  );
  return <Box sx={{ ...base, backgroundColor: '#efefef', color: '#a0a0a0' }}>{icon}</Box>;
}

// Campos do contato disponíveis pra mapear variáveis (ids alinhados ao snapshot do backend:
// name/phone/email/document; colunas extras de CSV também viram chaves no extra_fields).
const CONTACT_FIELDS = [
  { id: 'name', name: 'Nome' },
  { id: 'phone', name: 'Telefone' },
  { id: 'email', name: 'E-mail' },
  { id: 'document', name: 'Documento' },
];
const SAMPLE_CONTACTS = [
  { name: 'João Silva', phone: '+5511999990001', email: 'joao@exemplo.com', document: '123.456.789-00' },
  { name: 'Maria Souza', phone: '+5511999990002', email: 'maria@exemplo.com', document: '987.654.321-00' },
  { name: '', phone: '+5511999990003', email: '', document: '456.789.123-00' },
];
const WEEKDAYS = [
  { id: 0, label: 'D' }, { id: 1, label: 'S' }, { id: 2, label: 'T' }, { id: 3, label: 'Q' },
  { id: 4, label: 'Q' }, { id: 5, label: 'S' }, { id: 6, label: 'S' },
];
const WEEKDAY_NAMES = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];
const NTH_LABELS = { 1: 'primeira', 2: 'segunda', 3: 'terceira', 4: 'quarta', '-1': 'última' };
// Idiomas disponíveis do template (mock). Custo unitário por categoria Meta (USD, referência mock).
const TIMEZONES = [{ id: 'America/Sao_Paulo', name: 'America/Sao_Paulo (GMT-3)' }, { id: 'America/Manaus', name: 'America/Manaus (GMT-4)' }, { id: 'America/New_York', name: 'America/New_York (GMT-5)' }, { id: 'Europe/Lisbon', name: 'Europe/Lisbon (GMT+0)' }];
const META_PRICING = { Marketing: 0.0625, Utility: 0.035, Authentication: 0.035 };

class MarketingBroadcastNew extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeStep: 0,
      loading: true,   // skeleton da tela inteira até catálogos (+ detalhe na edição) chegarem
      saving: false,
      notification: { open: false, variant: 'success', message: '' },
      // catálogos carregados do backend
      channels: [], senders: [], templates: [], lists: [], optinTemplates: [],
      loadingChannels: false, loadingSenders: false, loadingTemplates: false, loadingOptin: false,
      // dados
      name: '',
      deliveryMethod: 'whatsapp', // whatsapp (padrão) | sms | email
      messageBody: '',            // SMS: texto livre com placeholders {{n}}
      // E-mail
      emailMode: 'custom',        // custom (compõe no editor) | template (usa tb_email_template)
      emailTemplateId: '',
      emailSubject: '',
      emailBodyHtml: '',
      emailDevice: 'desktop',     // preview do corpo: 'desktop' | 'mobile'
      emailAdvanced: false,       // true = edita HTML cru (Monaco); false = WYSIWYG (Jodit)
      emailAssetUrls: {},         // id→URL temporária das imagens do template (só p/ PREVIEW; snapshot guarda o marcador)
      emailTemplates: [],         // catálogo de templates salvos (2DO)
      attachments: [],            // [{ attachmentId, name, contentType, size, path }]
      uploadingAttachment: false,
      channelId: '',
      senderId: '',
      // SMS: disparador (provedor) escolhido por transmissão — transparência da origem do envio.
      smsSenders: [],          // [{ configId, providerName, providerCode, pricePerSegment }]
      smsConfigId: '',         // '' = usa o padrão do tenant (fallback no backend)
      loadingSmsSenders: false,
      // E-mail: disparador (SMTP) escolhido por transmissão. '' = Padrão Mexx (tb_config).
      emailSenders: [],        // opções [{ senderId, name, fromMail, isDefault, dailyLimit, isMexxDefault }]
      emailSenderId: '',       // '' = Padrão Mexx
      loadingEmailSenders: false,
      // Modal "Configurar disparador" (CRUD leve dos SMTPs próprios).
      emailModalOpen: false,
      emailSendersManage: [],  // linhas do CRUD (sem senha)
      emailModalLoading: false,
      emailFormId: null,       // id em edição (null = novo)
      emailFormSaving: false,
      emailForm: this.blankEmailForm(),
      emailTestDest: '',       // destino do "testar envio"
      emailTesting: false,
      listId: '',
      templateId: '',
      optinTemplateId: '',   // template de opt-in (pré-selecionado no padrão do número; usuário pode trocar)
      language: '',     // idioma do template (req. seção 1)
      varMapping: {},   // { '{{1}}': { mode:'field'|'fixed', field:'nome', fixed:'', fallback:'' } }
      loadedStatus: '', // status original da transmissão na edição (draft|scheduled|...) — define rótulo do botão
      testPhone: '55',   // telefone de teste (WhatsApp/SMS) — já vem com o DDI do país (Brasil)
      testEmail: '',     // e-mail de teste (meio e-mail)
      savedDraftId: null,   // id do rascunho salvo inline no passo 4 (pra testar sem sair)
      // preview dos destinatários (modal paginado + download CSV) — reusa contatos da lista alvo
      rcpPreviewOpen: false, rcpPage: 0, rcpRpp: 25, rcpItems: [], rcpLoading: false, rcpDownloading: false,
      noOptinCount: null,   // contatos válidos sem opt-in p/ o número (null = carregando/indisponível)
      // Agendamento completo (uma vez OU recorrente — estilo Zendesk/Intercom).
      schedule: {
        mode: 'now', // now | once | recurring
        onceAt: '',
        timezone: 'America/Sao_Paulo',
        quietHours: false,
        slice: false,
        sliceBatch: 2000,    // tamanho do lote quando fatiar
        sliceInterval: 15,   // intervalo entre lotes (min)
        recurrence: {
          freq: 'weekly',           // daily | weekly | monthly
          time: '09:00',
          interval: 1,              // a cada N (dias/semanas/meses)
          weekdays: [3],            // weekly: dias selecionados (0=dom..6=sáb)
          monthlyMode: 'dayOfMonth',// dayOfMonth | nthWeekday
          dayOfMonth: 1,
          nth: 1,                   // 1..4 ou -1 (última)
          nthWeekday: 4,            // dia da semana (0..6)
          endMode: 'never',         // never | count | date — condição de término
          endCount: 5,              // count: nº total de execuções
          endDate: '',              // date: até DD/MM/AAAA (YYYY-MM-DD no state)
        },
      },
    };
  }

  get lang() { return (window.location.pathname.split('/')[1]) || 'pt'; }
  // Aba de origem (?tab=) pra "Voltar"/cancelar retornar pra ela; na edição cai no status carregado.
  get originTab() {
    const t = new URLSearchParams(this.props.location ? this.props.location.search : '').get('tab');
    return t || this.state.loadedStatus || '';
  }
  goMarketing = (tab) => this.props.history.push(`/${this.lang}/marketing${tab ? `?tab=${tab}` : ''}`);
  get editId() { return this.props.match && this.props.match.params ? this.props.match.params.id : null; }
  get isEdit() { return !!this.editId; }
  // id efetivo da transmissão (rota de edição OU rascunho salvo inline no passo 4).
  get currentId() { return this.editId || this.state.savedDraftId; }
  // Publicar (programar/enviar) = admin/global/coordenador. Técnico só salva rascunho.
  get userRoles() { const u = this.props.userSession; return (u && u.user && Array.isArray(u.user.userrole)) ? u.user.userrole : []; }
  canPublish = () => this.userRoles.some(ur => ur && ur.role && [0, 1, 4].includes(Number(ur.role.roletype)));

  // Autocomplete padrão do projeto, mantendo o state por id (filtra ao digitar).
  acField = (label, options, valueId, onPick, extra = {}) => (
    <SimpleAutoComplete label={label} options={options} size="small" stateName="ac"
      selected={options.find(o => o.id === valueId) || { id: 0, name: '' }}
      changeSelect={(n, v) => onPick(v && v.id ? v.id : '')} {...extra} />
  );

  componentDidMount() {
    this.loadCatalogs();
  }

  loadCatalogs = async () => {
    this.setState({ loadingChannels: true });

    // Catálogos independentes em paralelo (cada um tolera falha sem quebrar o wizard).
    // Canais: somente WhatsApp (message_provider_id = 2), ativos.
    const channelsP = GetAllActiveAndByMessageProvider(WHATSAPP_PROVIDER_ID)
      .then(raw => this.setState({ channels: asArray(raw).map(normalizeChannel) }))
      .catch(() => {})
      .finally(() => this.setState({ loadingChannels: false }));

    // Listas (endpoint próprio do marketing)
    const listsP = MarketingService.listLists()
      .then(result => {
        const lists = (result.data && result.data.success && Array.isArray(result.data.data)) ? result.data.data.map(normalizeList) : [];
        this.setState({ lists });
      })
      .catch(() => {});

    // Templates de e-mail salvos (2DO) — pro modo "usar template" do meio e-mail.
    const emailP = listEmailTemplates({})
      .then(et => {
        const rows = (et && et.success && Array.isArray(et.data)) ? et.data : [];
        this.setState({ emailTemplates: rows.map(t => ({ id: t.id, name: t.name, subject: t.subject, bodyHtml: t.bodyhtml ?? t.bodyHtml ?? '' })) });
      })
      .catch(() => {});

    // Preço de SMS do tenant (estimativa de custo real; sem preço configurado → mostra só nº de segmentos).
    const smsP = MarketingService.getSmsCost()
      .then(smsCost => {
        const price = (smsCost.data && smsCost.data.success && smsCost.data.data) ? smsCost.data.data.pricepersegment : null;
        this.setState({ smsPricePerSegment: price });
      })
      .catch(() => {});

    // Disparadores de SMS configurados no tenant (transparência: usuário escolhe o provedor por transmissão).
    this.setState({ loadingSmsSenders: true });
    const smsSendersP = MarketingService.getSmsSenders()
      .then(res => {
        const rows = (res.data && res.data.success && Array.isArray(res.data.data)) ? res.data.data.map(normalizeSmsSender) : [];
        this.setState(prev => ({
          smsSenders: rows,
          // Um único disparador → pré-seleciona (nada a escolher); vários → deixa o usuário decidir.
          smsConfigId: (!prev.smsConfigId && rows.length === 1) ? rows[0].configId : prev.smsConfigId,
          loadingSmsSenders: false,
        }));
      })
      .catch(() => this.setState({ smsSenders: [], loadingSmsSenders: false }));

    // Disparadores de e-mail (Padrão Mexx + SMTPs próprios) — transparência + seleção por transmissão.
    this.setState({ loadingEmailSenders: true });
    const emailSendersP = MarketingService.getEmailSenders()
      .then(res => {
        const rows = (res.data && res.data.success && Array.isArray(res.data.data)) ? res.data.data.map(normalizeEmailSender) : [];
        this.setState(prev => {
          // Pré-seleciona o SMTP próprio marcado como default; senão mantém '' (Padrão Mexx).
          const def = rows.find(r => !r.isMexxDefault && r.isDefault);
          return {
            emailSenders: rows,
            emailSenderId: (!prev.emailSenderId && def) ? def.senderId : prev.emailSenderId,
            loadingEmailSenders: false,
          };
        });
      })
      .catch(() => this.setState({ emailSenders: [], loadingEmailSenders: false }));

    try {
      await Promise.all([channelsP, listsP, emailP, smsP, smsSendersP, emailSendersP]);
      // Na edição, só revela a tela depois que o detalhe + dependentes (número/templates) chegaram.
      if (this.isEdit) await this.loadBroadcastForEdit();
    } finally {
      this.setState({ loading: false });
    }
  };

  // Números do canal selecionado (registros associados ao canal). Depende do canal.
  loadSenders = (channelId, after) => {
    if (!channelId) { this.setState({ senders: [], loadingSenders: false }, after); return Promise.resolve(); }
    this.setState({ loadingSenders: true });
    return getPhonesAssociations(channelId)
      .then(raw => {
        const rows = asArray(raw && raw.data ? raw.data : raw);
        const senders = rows
          .filter(r => r && r.registrationnumberrequest && (r.active === undefined || r.active) )
          .map(r => ({ id: r.registrationnumberrequest.id, phone: r.registrationnumberrequest.phonenumber ?? '', name: '' }));
        this.setState({ senders, loadingSenders: false }, after);
      })
      .catch(() => this.setState({ senders: [], loadingSenders: false }, after));
  };

  loadBroadcastForEdit = async () => {
    try {
      const result = await MarketingService.getBroadcast(this.editId);
      if (!(result.data && result.data.success)) return;
      const d = normalizeBroadcastDetail(result.data.data);
      // varMapping array -> objeto { '{{n}}': {mode,field,fixed,fallback} }
      const varMapping = {};
      (d.varMapping || []).forEach(v => { varMapping[v.placeholder] = { mode: v.mode, field: v.field, fixed: v.fixed, fallback: v.fallback }; });
      this.setState(prev => ({
        name: d.name || '', deliveryMethod: d.deliveryMethod || 'whatsapp', messageBody: d.messageBody || '',
        emailMode: d.emailMode || 'custom', emailTemplateId: d.emailTemplateId || '',
        emailSubject: d.emailSubject || '', emailBodyHtml: d.emailBodyHtml || '',
        attachments: Array.isArray(d.attachments) ? d.attachments : [],
        channelId: d.channelId || '', senderId: d.senderId || '', listId: d.listId || '',
        smsConfigId: d.smsConfigId || prev.smsConfigId,
        emailSenderId: d.emailSenderId || prev.emailSenderId,
        templateId: d.templateId || '', optinTemplateId: d.optinTemplateId || '', language: d.language || '', varMapping, loadedStatus: d.status || '',
        schedule: { ...prev.schedule, ...d.schedule },
      }), this.loadEmailAssetUrls);   // preview: resolve as imagens do template salvo (marcador→URL temporária)
      // Dependentes do WhatsApp (número → templates/opt-in): aguarda pra tela revelar já preenchida.
      if (d.channelId) await this.loadSenders(d.channelId);
      if (d.senderId) await Promise.all([this.loadTemplates(d.senderId), this.loadOptinTemplates(d.senderId, true)]);
      this.loadNoOptIn();   // contagem secundária (tem seu próprio indicador) — não segura a tela
    } catch (err) { Api.kickoff(err); }
  };

  loadTemplates = (senderId) => {
    if (!senderId) { this.setState({ templates: [] }); return Promise.resolve(); }
    this.setState({ loadingTemplates: true });
    return getApprovedByRegistrationNumberRequestId(senderId)
      .then(raw => this.setState({ templates: asArray(raw).map(normalizeTemplate), loadingTemplates: false }))
      .catch(() => this.setState({ templates: [], loadingTemplates: false }));
  };

  // Templates de opt-in aprovados do número. Pré-seleciona o padrão (system=true) se o usuário ainda não escolheu.
  loadOptinTemplates = (senderId, keepSelection = false) => {
    if (!senderId) { this.setState({ optinTemplates: [], optinTemplateId: '' }); return Promise.resolve(); }
    this.setState({ loadingOptin: true });
    return getApprovedOptInByRegistrationNumberRequestId(senderId)
      .then(raw => {
        const optinTemplates = asArray(raw).map(normalizeTemplate);
        this.setState(prev => {
          const stillValid = keepSelection && optinTemplates.some(t => t.id === prev.optinTemplateId);
          const def = optinTemplates.find(t => t.system) || optinTemplates[0];
          return {
            optinTemplates,
            optinTemplateId: stillValid ? prev.optinTemplateId : (def ? def.id : ''),
            loadingOptin: false,
          };
        });
      })
      .catch(() => this.setState({ optinTemplates: [], optinTemplateId: '', loadingOptin: false }));
  };

  // Sem endpoint de unicidade de nome no V1 — a validação de duplicidade fica desativada.
  isDuplicateName = () => false;

  // Estimativa de custo (req. seção 4/5): nº de válidos × preço unitário da categoria Meta.
  costEstimate = () => {
    const list = this.selectedList();
    const count = list ? list.valid : 0;
    if (this.state.deliveryMethod === 'sms') {
      const segments = this.smsSegments((this.state.messageBody || '').length) || 1;
      const price = this.state.smsPricePerSegment;   // preço real do tenant (tb_config_sms)
      const priceKnown = price != null && !isNaN(Number(price)) && Number(price) > 0;
      const unit = priceKnown ? Number(price) * segments : 0;
      const sender = this.selectedSmsSender();
      return { count, unit, total: unit * count, sms: true, segments, priceKnown, senderName: sender ? this.smsSenderLabel(sender) : '' };
    }
    if (this.state.deliveryMethod === 'email') {
      // E-mail via SMTP próprio do tenant — sem custo por envio.
      return { count, unit: 0, total: 0, email: true };
    }
    const unit = META_PRICING.Marketing;   // feature de marketing; preço de referência Meta
    return { count, unit, total: unit * count };
  };

  // helpers de agendamento
  patchSchedule = (patch) => this.setState(p => ({ schedule: { ...p.schedule, ...patch } }));
  patchRecurrence = (patch) => this.setState(p => ({ schedule: { ...p.schedule, recurrence: { ...p.schedule.recurrence, ...patch } } }));
  toggleWeekday = (id) => {
    const cur = this.state.schedule.recurrence.weekdays;
    this.patchRecurrence({ weekdays: cur.includes(id) ? cur.filter(d => d !== id) : [...cur, id].sort() });
  };

  // Resumo legível da recorrência (o que o concorrente grande mostra pro usuário).
  recurrenceSummary = () => {
    const { schedule } = this.state;
    if (schedule.mode === 'now') return this.props.intl.formatMessage({ id: 'marketing.schedule.summary.now' });
    if (schedule.mode === 'once') return schedule.onceAt ? `${this.props.intl.formatMessage({ id: 'marketing.schedule.summary.once' })} ${schedule.onceAt.replace('T', ' ')}` : '—';
    const r = schedule.recurrence;
    const every = r.interval > 1 ? `${r.interval} ` : '';
    let base;
    if (r.freq === 'daily') base = `A cada ${every}dia(s) às ${r.time}`;
    else if (r.freq === 'weekly') {
      const days = r.weekdays.length ? r.weekdays.map(d => WEEKDAY_NAMES[d]).join(', ') : '—';
      base = `${r.interval > 1 ? `A cada ${r.interval} semanas` : 'Toda semana'} (${days}) às ${r.time}`;
    }
    else if (r.monthlyMode === 'dayOfMonth') base = `Mensalmente no dia ${r.dayOfMonth} às ${r.time}`;
    else base = `Mensalmente na ${NTH_LABELS[r.nth]} ${WEEKDAY_NAMES[r.nthWeekday]} às ${r.time}`;
    // Condição de término
    let end = '';
    if (r.endMode === 'count' && r.endCount) end = ` · até ${r.endCount} execuç${r.endCount > 1 ? 'ões' : 'ão'}`;
    else if (r.endMode === 'date' && r.endDate) end = ` · até ${r.endDate.split('-').reverse().join('/')}`;
    return base + end;
  };
  get steps() {
    const { intl } = this.props;
    // Passo 2: "Template" só faz sentido no WhatsApp (modelo Meta); SMS/e-mail são "Conteúdo".
    const isWhats = !['sms', 'email'].includes(this.state.deliveryMethod);
    return [
      intl.formatMessage({ id: 'marketing.step.data' }),
      intl.formatMessage({ id: 'marketing.step.recipients' }),
      intl.formatMessage({ id: isWhats ? 'marketing.step.template' : 'marketing.step.content' }),
      intl.formatMessage({ id: 'marketing.step.review' }),
      intl.formatMessage({ id: 'marketing.step.schedule' }),
    ];
  }

  selectedTemplate = () => this.state.templates.find(t => t.id === this.state.templateId);
  selectedList = () => {
    const l = this.state.lists.find(x => x.id === this.state.listId);
    return l ? { ...l, invalid: Math.max(0, (l.total || 0) - (l.valid || 0)) } : undefined;
  };

  // "Sem opt-in": só calcula quando há lista E número selecionados (depende do número de envio).
  loadNoOptIn = () => {
    const { listId, senderId } = this.state;
    if (!listId || !senderId) { this.setState({ noOptinCount: null }); return; }
    this.setState({ noOptinCount: null });
    MarketingService.getNoOptInCount(listId, senderId)
      .then(r => {
        const c = (r.data && r.data.success && r.data.data) ? (r.data.data.count ?? 0) : 0;
        this.setState({ noOptinCount: c });
      })
      .catch(() => this.setState({ noOptinCount: null }));
  };

  // Preview dos destinatários: abre o modal e carrega a página (paginação no servidor; total = list.total).
  openRecipientsPreview = () => { if (!this.state.listId) return; this.setState({ rcpPreviewOpen: true, rcpPage: 0 }, () => this.loadRecipientsPage(0, this.state.rcpRpp)); };
  closeRecipientsPreview = () => this.setState({ rcpPreviewOpen: false });
  loadRecipientsPage = (page, rpp) => {
    this.setState({ rcpLoading: true });
    MarketingService.getListContacts(this.state.listId, page + 1, rpp)
      .then(result => {
        const items = (result.data && result.data.success && Array.isArray(result.data.data))
          ? result.data.data.map(c => ({ name: c.name, phone: c.phone, email: c.email })) : [];
        this.setState({ rcpItems: items, rcpLoading: false });
      })
      .catch(() => this.setState({ rcpItems: [], rcpLoading: false }));
  };
  changeRcpPage = (page) => this.setState({ rcpPage: page }, () => this.loadRecipientsPage(page, this.state.rcpRpp));
  changeRcpRpp = (rpp) => this.setState({ rcpRpp: rpp, rcpPage: 0 }, () => this.loadRecipientsPage(0, rpp));
  downloadRecipientsCsv = async () => {
    if (!this.state.listId) return;
    this.setState({ rcpDownloading: true });
    try {
      const result = await MarketingService.exportListCsv(this.state.listId);
      const blob = new Blob([result.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `marketing-lista-${this.state.listId}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { Api.kickoff(err); }
    finally { this.setState({ rcpDownloading: false }); }
  };

  setVarMapping = (v, patch) => this.setState(prev => ({
    varMapping: { ...prev.varMapping, [v]: { mode: 'field', field: '', fixed: '', fallback: '', ...prev.varMapping[v], ...patch } }
  }));

  // Resolve uma variável pra um contato amostra (preview real, como pede o documento).
  resolveVar = (v, contact) => {
    const m = this.state.varMapping[v] || { mode: 'field', field: '', fallback: '' };
    if (m.mode === 'fixed') return m.fixed || `[${v}]`;
    const val = contact[m.field];
    return (val && String(val).trim()) ? val : (m.fallback || `[${m.field}]`);
  };

  renderPreview = (contact) => {
    const tpl = this.selectedTemplate();
    if (!tpl) return '';
    let body = tpl.body;
    tpl.vars.forEach(v => { body = body.split(v).join(this.resolveVar(v, contact)); });
    return body;
  };

  // ===== SMS (meio de envio alternativo) =====
  // Troca o meio de envio; reseta o mapeamento de variáveis (vars diferem entre template e SMS).
  setDeliveryMethod = (method) => {
    if (!method || method === this.state.deliveryMethod) return;
    this.setState({ deliveryMethod: method, varMapping: {} });
  };
  // Placeholders {{n}} presentes no corpo do SMS (mesmo regex do template).
  smsVars = () => Array.from(new Set((String(this.state.messageBody || '').match(/\{\{\s*\d+\s*\}\}/g) || [])));
  // Insere a próxima variável sequencial ({{maxExistente+1}}) no fim do corpo.
  insertSmsVar = () => {
    const nums = this.smsVars().map(v => parseInt(v.replace(/\D/g, ''), 10)).filter(n => !isNaN(n));
    const next = (nums.length ? Math.max(...nums) : 0) + 1;
    this.setState(p => ({ messageBody: `${p.messageBody || ''}{{${next}}}` }));
  };
  // Prévia do SMS pra um contato amostra (resolve os {{n}} como o renderPreview do template).
  renderPreviewSms = (contact) => {
    let body = this.state.messageBody || '';
    this.smsVars().forEach(v => { body = body.split(v).join(this.resolveVar(v, contact)); });
    return body;
  };
  // Nº de segmentos SMS (160 GSM no 1º; 153/segmento em multi). Aproximação (ignora unicode) — ok p/ estimativa.
  smsSegments = (len) => (len <= 0 ? 0 : (len <= 160 ? 1 : Math.ceil(len / 153)));

  // ===== E-mail (meio de envio) =====
  emailVisualRef = React.createRef();   // Jodit (WYSIWYG)
  emailCodeRef = React.createRef();     // Monaco (HTML avançado)
  _emailAssetRequested = new Set();     // ids já buscados p/ o PREVIEW (dedup — evita refetch por keystroke)
  // Imagens do template (mexx-email-asset://{id}): busca a URL temporária SÓ pro preview (o snapshot guarda
  // o marcador; o 2Talk resolve e embute inline no envio). Promise chain (sem async arrow — babel 7.12.3).
  loadEmailAssetUrls = () => {
    const html = String(this.state.emailBodyHtml || '');
    const ids = [];
    const re = /mexx-email-asset:\/\/([1-9][0-9]*)/gi;
    let m;
    while ((m = re.exec(html)) !== null) {
      const id = Number(m[1]);
      if (!this._emailAssetRequested.has(id)) ids.push(id);
    }
    if (!ids.length) return;
    ids.forEach(id => this._emailAssetRequested.add(id));
    getAssetUrls(ids).then(r => {
      if (!r || !r.success || !(r.data || []).length) return;
      const next = { ...this.state.emailAssetUrls };
      (r.data || []).forEach(a => { if (a && a.url) next[a.id ?? a.Id] = a.url; });
      this.setState({ emailAssetUrls: next });
    }).catch(() => { /* preview pode seguir com marcador */ });
  };
  // Placeholders {{n}} presentes no assunto OU no corpo HTML.
  emailVars = () => Array.from(new Set(((String(this.state.emailSubject || '') + ' ' + String(this.state.emailBodyHtml || '')).match(/\{\{\s*\d+\s*\}\}/g)) || []));
  // Insere a próxima variável sequencial no assunto ('subject') ou no corpo ('body').
  insertEmailVar = (target) => {
    const nums = this.emailVars().map(v => parseInt(v.replace(/\D/g, ''), 10)).filter(n => !isNaN(n));
    const token = `{{${(nums.length ? Math.max(...nums) : 0) + 1}}}`;
    if (target === 'subject') { this.setState(p => ({ emailSubject: `${p.emailSubject || ''}${token}` })); return; }
    // Corpo: insere no CURSOR do editor ativo — anexar à string HTML quebraria o documento completo.
    const editor = this.state.emailAdvanced ? this.emailCodeRef.current : this.emailVisualRef.current;
    if (editor && editor.insertText) editor.insertText(token);
    else this.setState(p => ({ emailBodyHtml: `${p.emailBodyHtml || ''}${token}` }));
  };
  // Ao escolher um template salvo, busca o HTML já renderizado e preenche o snapshot (assunto+corpo).
  onPickEmailTemplate = (id) => {
    this.setState({ emailTemplateId: id, varMapping: {} });
    if (!id) return;
    // Render no 2DO: layout + assets aplicados, {{x}} PRESERVADOS (resolvidos por destinatário no envio).
    renderEmailTemplateForMarketing(id).then(r => {
      const t = (r && r.success) ? r.data : null;
      if (!t) return;
      const subject = t.subject ?? t.Subject ?? '';
      const html = t.html ?? t.Html ?? '';
      this.setState({ emailSubject: subject, emailBodyHtml: html }, this.loadEmailAssetUrls);
      // Avisa na hora se o template veio com variáveis não suportadas (ex.: tokens de chamado) — o editor
      // está liberado, então o usuário troca por {{n}} e segue. A trava do passo garante a correção.
      const invalid = this.invalidVarsIn(`${subject} ${html}`);
      if (invalid.length) this.setState({ notification: { open: true, variant: 'warning', message: this.props.intl.formatMessage({ id: 'marketing.vars.invalidPick' }, { vars: invalid.join(', ') }) } });
    }).catch(() => { /* mantém vazio; usuário pode compor */ });
  };
  // Corpo HTML tem conteúdo real (ignora tags)?
  emailBodyHasContent = () => String(this.state.emailBodyHtml || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0;
  // Prévia: resolve {{n}} em assunto/corpo pra um contato amostra.
  resolveEmailText = (text, contact) => {
    let out = String(text || '');
    this.emailVars().forEach(v => { out = out.split(v).join(this.resolveVar(v, contact)); });
    // Imagens do template: troca o marcador pela URL temporária (só exibição) — se ainda não resolveu, mantém.
    const urls = this.state.emailAssetUrls || {};
    out = out.replace(/mexx-email-asset:\/\/([1-9][0-9]*)/gi, (marker, id) => urls[Number(id)] || marker);
    return out;
  };
  // Upload de anexo via endpoint dedicado de marketing (2DO) — devolve URL servível (sem calledId).
  addAttachment = (file) => {
    if (!file) return;
    this.setState({ uploadingAttachment: true });
    uploadMarketingAttachment(file).then(res => {
      if (res && res.success && res.data) {
        const d = res.data;
        const att = {
          attachmentId: d.id ?? d.attachmentid ?? d.attachmentId ?? null,
          name: file.name,
          contentType: file.type || (d.type ?? d.contenttype ?? ''),
          size: file.size,
          path: d.path ?? d.url ?? d.filepath ?? d.pathname ?? null,
        };
        this.setState(p => ({ attachments: [...p.attachments, att], uploadingAttachment: false }));
      } else {
        this.setState({ uploadingAttachment: false, notification: { open: true, variant: 'error', message: this.props.intl.formatMessage({ id: 'marketing.email.uploadError' }) } });
      }
    }).catch(() => this.setState({ uploadingAttachment: false, notification: { open: true, variant: 'error', message: this.props.intl.formatMessage({ id: 'marketing.email.uploadError' }) } }));
  };
  removeAttachment = (idx) => this.setState(p => ({ attachments: p.attachments.filter((_, i) => i !== idx) }));

  // Toda variável ({{n}}) precisa estar mapeada: 'fixed' com valor OU 'field' com campo escolhido.
  varsMapped = (vars) => vars.every(v => {
    const m = this.state.varMapping[v];
    if (!m) return false;
    return m.mode === 'fixed' ? !!(m.fixed && m.fixed.trim()) : !!(m.field && m.field.trim());
  });

  // Marketing só aceita variáveis NUMÉRICAS {{n}} (mapeáveis a campo do contato). Qualquer {{...}} não-numérico
  // (ex.: token de chamado {{nome_solicitante}}) é INVÁLIDO — comum em templates de e-mail herdados de chamado.
  invalidVarsIn = (text) => Array.from(new Set((String(text || '').match(/\{\{[^{}]+\}\}/g) || [])
    .filter(t => !/^\{\{\s*\d+\s*\}\}$/.test(t))));
  // Variáveis inválidas do conteúdo atual por canal. WhatsApp = template Meta (read-only, só {{n}}) → não valida.
  currentInvalidVars = () => {
    if (this.state.deliveryMethod === 'sms') return this.invalidVarsIn(this.state.messageBody);
    if (this.state.deliveryMethod === 'email') return this.invalidVarsIn(`${this.state.emailSubject || ''} ${this.state.emailBodyHtml || ''}`);
    return [];
  };
  renderInvalidVarsAlert = () => {
    const invalid = this.currentInvalidVars();
    if (!invalid.length) return null;
    return (
      <Alert severity="error" sx={{ mt: 2, borderRadius: '10px' }}>
        {this.props.intl.formatMessage({ id: 'marketing.vars.invalid' }, { vars: invalid.join(', ') })}
      </Alert>
    );
  };

  canNext = () => {
    const isSms = this.state.deliveryMethod === 'sms';
    const isEmail = this.state.deliveryMethod === 'email';
    switch (this.state.activeStep) {
      case 0: {
        const base = this.state.name.trim() && !this.isDuplicateName();
        // SMS/E-mail não exigem canal/número WhatsApp; WhatsApp mantém a regra atual.
        return (isSms || isEmail) ? !!base : (base && this.state.channelId && this.state.senderId);
      }
      case 1: return !!this.state.listId;
      case 2: {
        // Bloqueia variável não suportada (token de chamado {{xxx}}); marketing só aceita {{n}}.
        if ((isSms || isEmail) && this.currentInvalidVars().length) return false;
        if (isSms) {
          if (!(this.state.messageBody && this.state.messageBody.trim())) return false;
          return this.varsMapped(this.smsVars());
        }
        if (isEmail) {
          if (!(this.state.emailSubject && this.state.emailSubject.trim())) return false;
          if (!this.emailBodyHasContent()) return false;
          return this.varsMapped(this.emailVars());
        }
        if (!this.state.templateId) return false;
        const tpl = this.selectedTemplate();
        return this.varsMapped(tpl ? tpl.vars : []);
      }
      default: return true;
    }
  };

  next = () => this.setState(p => ({ activeStep: Math.min(p.activeStep + 1, 4) }), this.loadEmailAssetUrls);
  back = () => this.setState(p => ({ activeStep: Math.max(p.activeStep - 1, 0) }));
  // Atalho na edição: clicar no passo do stepper pula direto pra ele (dados já carregados).
  goToStep = (i) => { if (this.isEdit) this.setState({ activeStep: i }, this.loadEmailAssetUrls); };

  // Sugere um nome com data/hora atual (ex.: "Envio em lote em sexta-feira, 26/06/2026 às 15:44").
  // Usa toLocaleDateString (nativo) p/ o dia da semana no idioma correto, sem depender de locale do moment.
  suggestName = () => {
    const lang = this.lang === 'pt' ? 'pt-BR' : this.lang === 'es' ? 'es-ES' : 'en-US';
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const weekday = now.toLocaleDateString(lang, { weekday: 'long' });
    const date = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()}`;
    const time = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
    const method = this.props.intl.formatMessage({ id: 'marketing.method.' + (this.state.deliveryMethod || 'whatsapp') });
    this.setState({ name: this.props.intl.formatMessage({ id: 'marketing.create.suggestedName' }, { method, weekday, date, time }) });
  };
  cancel = () => this.goMarketing(this.originTab);

  sendTest = async () => {
    const { intl } = this.props;
    if (!this.currentId) {
      this.setState({ notification: { open: true, variant: 'error', message: intl.formatMessage({ id: 'marketing.test.saveFirst' }) } });
      return;
    }
    const isEmail = this.state.deliveryMethod === 'email';
    const destination = isEmail ? (this.state.testEmail || '').trim() : (this.state.testPhone || '').replace(/\D/g, '');
    if (isEmail ? !/^\S+@\S+\.\S+$/.test(destination) : destination.length < 12) return;
    this.setState({ saving: true });
    try {
      const result = await MarketingService.testBroadcast(this.currentId, destination);
      if (result.data && result.data.success) {
        this.setState({ notification: { open: true, variant: 'success', message: intl.formatMessage({ id: 'marketing.test.sent' }) } });
      } else {
        this.setState({ notification: { open: true, variant: 'error', message: (result.data && result.data.errors && result.data.errors[0]) || intl.formatMessage({ id: 'marketing.toast.error' }) } });
      }
    } catch (err) { Api.kickoff(err); }
    finally { this.setState({ saving: false }); }
  };

  buildModel = () => {
    const isSms = this.state.deliveryMethod === 'sms';
    const isEmail = this.state.deliveryMethod === 'email';
    const tpl = this.selectedTemplate();
    // Variáveis: SMS = corpo; E-mail = assunto+corpo; WhatsApp = template (fallback p/ chaves mapeadas).
    const vars = isSms ? this.smsVars() : isEmail ? this.emailVars() : (tpl ? tpl.vars : Object.keys(this.state.varMapping));
    const varMapping = vars.map(v => {
      const m = this.state.varMapping[v] || { mode: 'field', field: '', fixed: '', fallback: '' };
      return { placeholder: v, mode: m.mode, field: m.field, fixed: m.fixed, fallback: m.fallback };
    });
    return {
      name: this.state.name,
      deliveryMethod: this.state.deliveryMethod,
      smsConfigId: isSms ? (this.state.smsConfigId || null) : null,
      emailSenderId: isEmail ? (this.state.emailSenderId || null) : null,
      messageBody: isSms ? this.state.messageBody : null,
      emailMode: isEmail ? this.state.emailMode : null,
      emailTemplateId: isEmail && this.state.emailMode === 'template' ? (this.state.emailTemplateId || null) : null,
      emailSubject: isEmail ? this.state.emailSubject : null,
      emailBodyHtml: isEmail ? this.state.emailBodyHtml : null,
      attachments: isEmail ? this.state.attachments : [],
      channelId: this.state.channelId, senderId: this.state.senderId,
      templateId: this.state.templateId, language: this.state.language, listId: this.state.listId,
      optinTemplateId: this.state.optinTemplateId || null,
      varMapping, schedule: this.state.schedule,
    };
  };

  // target: 'draft' (fica Rascunho) | 'active' (Enviar agora) | 'scheduled' (Programar).
  // Salvar rascunho SEM sair da tela (passo 4) — habilita testar logo em seguida.
  saveDraftInline = async () => {
    const { intl } = this.props;
    const model = this.buildModel();
    this.setState({ saving: true });
    try {
      const id = this.currentId;
      const result = id
        ? await MarketingService.updateBroadcast(id, model, 'draft')
        : await MarketingService.createBroadcast(model, 'draft');
      if (result.data && result.data.success) {
        const newId = id || (result.data.data && result.data.data.id);
        this.setState({ saving: false, savedDraftId: newId || id, notification: { open: true, variant: 'success', message: intl.formatMessage({ id: 'marketing.toast.draftSaved' }) } });
      } else {
        this.setState({ saving: false, notification: { open: true, variant: 'error', message: (result.data && result.data.errors && result.data.errors[0]) || intl.formatMessage({ id: 'marketing.toast.error' }) } });
      }
    } catch (err) {
      this.setState({ saving: false, notification: { open: true, variant: 'error', message: this.extractError(err) } });
      Api.kickoff(err);
    }
  };

  submit = async (target) => {
    const { intl } = this.props;
    const action = target === 'draft' ? 'draft' : target === 'scheduled' ? 'schedule' : 'send';
    const model = this.buildModel();
    this.setState({ saving: true });
    try {
      const id = this.currentId;
      const result = id
        ? await MarketingService.updateBroadcast(id, model, action)
        : await MarketingService.createBroadcast(model, action);
      if (result.data && result.data.success) {
        const msgId = action === 'draft' ? 'marketing.toast.draftSaved' : action === 'schedule' ? 'marketing.toast.scheduled' : 'marketing.create.success';
        this.setState({ saving: false, notification: { open: true, variant: 'success', message: intl.formatMessage({ id: msgId }) } });
        // Após salvar, vai pra aba do status resultante (draft|scheduled|active = o próprio target).
        setTimeout(() => this.goMarketing(target), 900);
      } else {
        this.setState({ saving: false, notification: { open: true, variant: 'error', message: (result.data && result.data.errors && result.data.errors[0]) || intl.formatMessage({ id: 'marketing.toast.error' }) } });
      }
    } catch (err) {
      // CustomResponse devolve BadRequest (400) nas críticas → cai aqui; exibe o erro do back no toast vermelho.
      this.setState({ saving: false, notification: { open: true, variant: 'error', message: this.extractError(err) } });
      Api.kickoff(err);
    }
  };

  // Mensagem de erro vinda do backend (CustomResponse 400) ou fallback genérico.
  extractError = (err) => {
    const errs = err && err.response && err.response.data && err.response.data.errors;
    return (Array.isArray(errs) && errs[0]) || this.props.intl.formatMessage({ id: 'marketing.toast.error' });
  };

  field = (props) => <TextField fullWidth size="small" InputLabelProps={{ shrink: true }} {...props} />;

  // Form em branco do disparador de e-mail (modal CRUD). Método NORMAL (usado no construtor via this.state).
  blankEmailForm() {
    return { name: '', fromMail: '', hostMail: '', portMail: 587, userMail: '', password: '', enableSsl: true, dailyLimit: '', isDefault: false, active: true };
  }

  // Disparador (provedor) do SMS — transparência da origem do envio.
  // 0 configurados → aviso + atalho pro cadastro; 1 → indicador travado; 2+ → seletor.
  smsSenderLabel = (s) => `${s.providerName || s.providerCode || '—'}`;
  selectedSmsSender = () => this.state.smsSenders.find(s => s.configId === this.state.smsConfigId) || null;
  renderSmsSenderBlock() {
    const { intl } = this.props;
    const senders = this.state.smsSenders;

    if (this.state.loadingSmsSenders) {
      return <Skeleton variant="rounded" height={48} animation="wave" />;
    }

    // Nenhum disparador configurado: segue com o padrão do tenant; avisa e oferece atalho pro cadastro.
    if (senders.length === 0) {
      return (
        <Alert severity="info" action={
          <Button color="inherit" size="small" sx={{ textTransform: 'none' }} onClick={() => this.props.history.push(`/${this.lang}/config-edit`)}>
            {intl.formatMessage({ id: 'marketing.sms.sender.configure' })}
          </Button>
        }>{intl.formatMessage({ id: 'marketing.sms.senderNote' })}</Alert>
      );
    }

    // Um único disparador: nada a escolher — mostra travado (transparência de qual provedor dispara).
    if (senders.length === 1) {
      const only = senders[0];
      return (
        <Box sx={{ p: 1.25, borderRadius: '10px', border: '1px solid #e0e0e0', backgroundColor: '#fafafa', display: 'flex', alignItems: 'center', gap: 1 }}>
          <SmsOutlinedIcon sx={{ fontSize: 18, color: '#9e9e9e' }} />
          <Typography sx={{ fontSize: 13, color: '#555' }}>
            {intl.formatMessage({ id: 'marketing.sms.sender' })}: <b>{this.smsSenderLabel(only)}</b>
          </Typography>
        </Box>
      );
    }

    // Vários disparadores: usuário escolhe por transmissão.
    return (
      <Box>
        {this.acField(intl.formatMessage({ id: 'marketing.sms.sender' }),
          senders.map(s => ({ id: s.configId, name: this.smsSenderLabel(s) })),
          this.state.smsConfigId,
          (id) => this.setState({ smsConfigId: id }))}
        <Typography sx={{ fontSize: 11, color: '#999', mt: 0.5 }}>{intl.formatMessage({ id: 'marketing.sms.sender.note' })}</Typography>
      </Box>
    );
  }

  // ===== Disparador de e-mail (Item C): transparência + seleção + CRUD leve no modal =====
  emailOptionLabel = (o) => (o.isMexxDefault ? this.props.intl.formatMessage({ id: 'marketing.email.sender.mexx' }) : (o.name || o.fromMail || '—'));
  emailOptionId = (o) => (o.isMexxDefault ? '' : o.senderId);
  selectedEmailOption = () => this.state.emailSenders.find(o => this.emailOptionId(o) === (this.state.emailSenderId || '')) || null;

  renderEmailSenderBlock() {
    const { intl } = this.props;
    if (this.state.loadingEmailSenders) return <Skeleton variant="rounded" height={48} animation="wave" />;

    const options = this.state.emailSenders.length
      ? this.state.emailSenders
      : [{ senderId: null, isMexxDefault: true, name: 'Padrão Mexx', dailyLimit: null }];
    const selected = this.selectedEmailOption() || options.find(o => o.isMexxDefault) || options[0];

    return (
      <Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            {this.acField(intl.formatMessage({ id: 'marketing.email.sender' }),
              options.map(o => ({ id: this.emailOptionId(o), name: this.emailOptionLabel(o) })),
              this.state.emailSenderId || '',
              (id) => this.setState({ emailSenderId: id || '' }))}
          </Box>
          <Button variant="outlined" startIcon={<SettingsOutlinedIcon />} onClick={() => this.openEmailModal()}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap', mt: 0.25, borderColor: '#cfcfcf', color: '#444', flexShrink: 0 }}>
            {intl.formatMessage({ id: 'marketing.email.sender.configure' })}
          </Button>
        </Box>
        {/* Transparência da origem + limite diário do disparador escolhido. */}
        <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 12, color: '#777' }}>
            {intl.formatMessage({ id: 'marketing.email.sender.dispatchedBy' })}: <b style={{ color: '#555' }}>{selected ? this.emailOptionLabel(selected) : '—'}</b>
          </Typography>
          {selected && selected.isMexxDefault && (
            <Chip size="small" label={intl.formatMessage({ id: 'marketing.email.sender.limit' }, { n: (selected.dailyLimit || 0).toLocaleString('pt-BR') })}
              sx={{ height: 20, fontSize: 11, backgroundColor: '#eef7e3', color: '#5b9b1e' }} />
          )}
          {selected && !selected.isMexxDefault && (
            <Chip size="small" label={selected.dailyLimit
              ? intl.formatMessage({ id: 'marketing.email.sender.limit' }, { n: Number(selected.dailyLimit).toLocaleString('pt-BR') })
              : intl.formatMessage({ id: 'marketing.email.sender.noLimit' })}
              sx={{ height: 20, fontSize: 11, backgroundColor: '#f0f0f0', color: '#666' }} />
          )}
        </Box>
        {/* Aviso de limites (Item E): Padrão Mexx = infra compartilhada; acima do volume, use SMTP próprio. */}
        {selected && selected.isMexxDefault && (
          <Alert severity="info" sx={{ mt: 1, py: 0, fontSize: 12, '& .MuiAlert-message': { fontSize: 12 } }}>
            {intl.formatMessage({ id: 'marketing.email.limit.advisory' }, { n: (selected.dailyLimit || 0).toLocaleString('pt-BR') })}
          </Alert>
        )}
      </Box>
    );
  }

  // --- modal "Configurar disparador" (CRUD leve dos SMTPs próprios) ---
  openEmailModal() {
    this.setState({ emailModalOpen: true, emailFormId: null, emailForm: this.blankEmailForm() });
    this.loadEmailSendersManage();
  }
  closeEmailModal = () => this.setState({ emailModalOpen: false });
  patchEmailForm = (patch) => this.setState(p => ({ emailForm: { ...p.emailForm, ...patch } }));

  loadEmailSendersManage() {
    this.setState({ emailModalLoading: true });
    return MarketingService.listEmailSenders()
      .then(res => {
        const rows = (res.data && res.data.success && Array.isArray(res.data.data)) ? res.data.data.map(normalizeEmailSenderRow) : [];
        this.setState({ emailSendersManage: rows, emailModalLoading: false });
      })
      .catch(() => this.setState({ emailSendersManage: [], emailModalLoading: false }));
  }

  newEmailSender = () => this.setState({ emailFormId: null, emailForm: this.blankEmailForm() });
  editEmailSender = (row) => this.setState({
    emailFormId: row.id,
    // Senha nunca volta do backend → campo em branco (em branco no save = mantém a atual).
    emailForm: { name: row.name, fromMail: row.fromMail, hostMail: row.hostMail, portMail: row.portMail || 587, userMail: row.userMail, password: '', enableSsl: row.enableSsl, dailyLimit: row.dailyLimit ?? '', isDefault: row.isDefault, active: row.active },
  });

  saveEmailSender() {
    const { intl } = this.props;
    const f = this.state.emailForm;
    if (!f.name || !f.fromMail || !f.hostMail || !f.portMail || (!this.state.emailFormId && !f.password)) {
      this.setState({ notification: { open: true, variant: 'error', message: intl.formatMessage({ id: 'marketing.email.sender.required' }) } });
      return;
    }
    this.setState({ emailFormSaving: true });
    const id = this.state.emailFormId;
    const req = id ? MarketingService.updateEmailSender(id, f) : MarketingService.createEmailSender(f);
    req
      .then(result => {
        if (result.data && result.data.success) {
          this.setState({ emailFormSaving: false, emailFormId: null, emailForm: this.blankEmailForm(),
            notification: { open: true, variant: 'success', message: intl.formatMessage({ id: 'marketing.toast.draftSaved' }) } });
          this.loadEmailSendersManage();
          this.refreshEmailSenderOptions();
        } else {
          this.setState({ emailFormSaving: false, notification: { open: true, variant: 'error', message: (result.data && result.data.errors && result.data.errors[0]) || intl.formatMessage({ id: 'marketing.toast.error' }) } });
        }
      })
      .catch(err => { this.setState({ emailFormSaving: false }); Api.kickoff(err); });
  }

  testEmailSend() {
    const { intl } = this.props;
    const f = this.state.emailForm;
    const dest = (this.state.emailTestDest || '').trim();
    if (!/^\S+@\S+\.\S+$/.test(dest)) {
      this.setState({ notification: { open: true, variant: 'error', message: intl.formatMessage({ id: 'marketing.email.sender.testDestReq' }) } });
      return;
    }
    this.setState({ emailTesting: true });
    MarketingService.testEmailSender(this.state.emailFormId, f, dest)
      .then(result => {
        const ok = result.data && result.data.success && result.data.data && result.data.data.ok;
        this.setState({ emailTesting: false, notification: {
          open: true,
          variant: ok ? 'success' : 'error',
          message: ok ? intl.formatMessage({ id: 'marketing.email.sender.testOk' }) : ((result.data && result.data.errors && result.data.errors[0]) || intl.formatMessage({ id: 'marketing.email.sender.testFail' })),
        } });
      })
      .catch(err => { this.setState({ emailTesting: false }); Api.kickoff(err); });
  }

  deleteEmailSender(id) {
    const { intl } = this.props;
    this.setState({ emailModalLoading: true });
    MarketingService.deleteEmailSender(id)
      .then(result => {
        if (result.data && result.data.success) {
          this.loadEmailSendersManage();
          this.refreshEmailSenderOptions(id);
        } else {
          this.setState({ emailModalLoading: false, notification: { open: true, variant: 'error', message: (result.data && result.data.errors && result.data.errors[0]) || intl.formatMessage({ id: 'marketing.toast.error' }) } });
        }
      })
      .catch(err => { this.setState({ emailModalLoading: false }); Api.kickoff(err); });
  }

  // Recarrega as opções do seletor após CRUD; se o disparador removido era o selecionado, volta pra Padrão Mexx.
  refreshEmailSenderOptions(removedId) {
    return MarketingService.getEmailSenders()
      .then(res => {
        const rows = (res.data && res.data.success && Array.isArray(res.data.data)) ? res.data.data.map(normalizeEmailSender) : [];
        this.setState(prev => ({
          emailSenders: rows,
          emailSenderId: (removedId != null && prev.emailSenderId === removedId) ? '' : prev.emailSenderId,
        }));
      })
      .catch(() => {});
  }

  renderStepData() {
    const { intl } = this.props;
    const F = this.field;
    const isWhats = this.state.deliveryMethod === 'whatsapp';
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 560 }}>
        {/* Meio de envio — define o resto do fluxo (WhatsApp = template Meta; SMS = texto livre). */}
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.75 }}>{intl.formatMessage({ id: 'marketing.field.deliveryMethod' })}</Typography>
          <ToggleButtonGroup exclusive size="small" value={this.state.deliveryMethod}
            onChange={(e, v) => this.setDeliveryMethod(v)}
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 2, gap: 0.75, borderRadius: '10px !important', border: '1px solid #e0e0e0 !important', mr: 1 }, '& .Mui-selected': { backgroundColor: '#eef7e3 !important', color: '#5b9b1e', borderColor: '#8BC34A !important' } }}>
            <ToggleButton value="whatsapp"><WhatsAppIcon sx={{ fontSize: 18 }} />{intl.formatMessage({ id: 'marketing.method.whatsapp' })}</ToggleButton>
            <ToggleButton value="sms"><SmsOutlinedIcon sx={{ fontSize: 18 }} />{intl.formatMessage({ id: 'marketing.method.sms' })}</ToggleButton>
            <ToggleButton value="email"><EmailOutlinedIcon sx={{ fontSize: 18 }} />{intl.formatMessage({ id: 'marketing.method.email' })}</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <F label={intl.formatMessage({ id: 'marketing.field.name' })} value={this.state.name} onChange={e => this.setState({ name: e.target.value })}
              error={this.isDuplicateName()} helperText={this.isDuplicateName() ? intl.formatMessage({ id: 'marketing.field.nameDup' }) : ''} />
          </Box>
          {/* Sugerir nome: só na inclusão (na edição o nome já existe). */}
          {!this.isEdit && (
            <Button variant="outlined" startIcon={<AutoAwesomeOutlinedIcon />} onClick={this.suggestName}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap', mt: 0.25, borderColor: '#cfcfcf', color: '#444', flexShrink: 0 }}>
              {intl.formatMessage({ id: 'marketing.create.suggestName' })}
            </Button>
          )}
        </Box>
        {/* Canal + número de envio: específicos do WhatsApp (SMS usa o provedor do tenant em tb_config_sms). */}
        {isWhats && this.acField(intl.formatMessage({ id: 'marketing.field.channel' }), this.state.channels, this.state.channelId,
          (id) => { this.setState({ channelId: id, senderId: '', templateId: '', varMapping: {}, senders: [], templates: [], optinTemplates: [], optinTemplateId: '', noOptinCount: null }); this.loadSenders(id); }, { loading: this.state.loadingChannels })}
        {isWhats && this.acField(intl.formatMessage({ id: 'marketing.field.sender' }), this.state.senders.map(s => ({ id: s.id, name: `${s.phone}${s.name ? ` (${s.name})` : ''}` })), this.state.senderId,
          (id) => { this.setState({ senderId: id, templateId: '', varMapping: {} }, this.loadNoOptIn); this.loadTemplates(id); this.loadOptinTemplates(id); }, { loading: this.state.loadingSenders })}
        {/* Sem número registrado ativo pro canal: avisa em vez de combo vazio silencioso */}
        {isWhats && this.state.channelId && !this.state.loadingSenders && this.state.senders.length === 0 && (
          <Alert severity="warning" action={
            <Button color="inherit" size="small" sx={{ textTransform: 'none' }} onClick={() => this.props.history.push(`/${this.lang}/channel`)}>
              {intl.formatMessage({ id: 'marketing.field.registerNumber' })}
            </Button>
          }>{intl.formatMessage({ id: 'marketing.field.noActiveNumber' })}</Alert>
        )}
        {this.state.deliveryMethod === 'sms' && this.renderSmsSenderBlock()}
        {this.state.deliveryMethod === 'email' && this.renderEmailSenderBlock()}
        {this.renderEmailSenderDialog()}
      </Box>
    );
  }

  renderEmailSenderDialog() {
    const { intl } = this.props;
    const f = this.state.emailForm;
    const rows = this.state.emailSendersManage;
    const tf = (label, value, onChange, props = {}) => (
      <TextField label={label} value={value} onChange={onChange} size="small" fullWidth InputLabelProps={{ shrink: true }} sx={{ mb: 1.5 }} {...props} />
    );
    return (
      <Dialog open={this.state.emailModalOpen} onClose={this.closeEmailModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}>
        <DialogTitle sx={{ fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailOutlinedIcon sx={{ color: '#8BC34A' }} /> {intl.formatMessage({ id: 'marketing.email.sender.manageTitle' })}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <div style={{ maxHeight: '64vh', overflowY: 'auto', padding: '16px 20px' }}>
            <Alert severity="info" sx={{ mb: 2, borderRadius: '10px', fontSize: 12, '& .MuiAlert-message': { fontSize: 12 } }}>
              {intl.formatMessage({ id: 'marketing.email.sender.modalNote' })}
            </Alert>
            {/* Lista dos disparadores próprios cadastrados */}
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#555', mb: 1 }}>{intl.formatMessage({ id: 'marketing.email.sender.yours' })}</Typography>
            {this.state.emailModalLoading ? (
              <Skeleton variant="rounded" height={56} animation="wave" />
            ) : rows.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: '#999', mb: 1.5 }}>{intl.formatMessage({ id: 'marketing.email.sender.empty' })}</Typography>
            ) : rows.map(r => (
              <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, mb: 1, border: '1px solid #e0e0e0', borderRadius: '10px' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {r.name}
                    {r.isDefault && <Chip size="small" label={intl.formatMessage({ id: 'marketing.email.sender.default' })} sx={{ height: 18, fontSize: 10, backgroundColor: '#eef7e3', color: '#5b9b1e' }} />}
                    {!r.active && <Chip size="small" label={intl.formatMessage({ id: 'marketing.email.sender.inactive' })} sx={{ height: 18, fontSize: 10, backgroundColor: '#f0f0f0', color: '#999' }} />}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.fromMail} · {r.hostMail}:{r.portMail}</Typography>
                </Box>
                <IconButton size="small" onClick={() => this.editEmailSender(r)}><EditOutlinedIcon sx={{ fontSize: 18 }} /></IconButton>
                <IconButton size="small" onClick={() => this.deleteEmailSender(r.id)}><DeleteOutlineIcon sx={{ fontSize: 18, color: '#d32f2f' }} /></IconButton>
              </Box>
            ))}

            <Divider sx={{ my: 1.5 }} />

            {/* Form novo/editar */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#555' }}>
                {this.state.emailFormId ? intl.formatMessage({ id: 'marketing.email.sender.edit' }) : intl.formatMessage({ id: 'marketing.email.sender.new' })}
              </Typography>
              {this.state.emailFormId && (
                <Button size="small" startIcon={<AddOutlinedIcon />} onClick={this.newEmailSender} sx={{ textTransform: 'none' }}>
                  {intl.formatMessage({ id: 'marketing.email.sender.new' })}
                </Button>
              )}
            </Box>
            {tf(intl.formatMessage({ id: 'marketing.email.sender.name' }), f.name, e => this.patchEmailForm({ name: e.target.value }))}
            {tf(intl.formatMessage({ id: 'marketing.email.sender.from' }), f.fromMail, e => this.patchEmailForm({ fromMail: e.target.value }), { type: 'email' })}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box sx={{ flex: 2 }}>{tf(intl.formatMessage({ id: 'marketing.email.sender.host' }), f.hostMail, e => this.patchEmailForm({ hostMail: e.target.value }))}</Box>
              <Box sx={{ flex: 1 }}>{tf(intl.formatMessage({ id: 'marketing.email.sender.port' }), f.portMail, e => this.patchEmailForm({ portMail: e.target.value.replace(/\D/g, '') }), { type: 'number' })}</Box>
            </Box>
            {tf(intl.formatMessage({ id: 'marketing.email.sender.user' }), f.userMail, e => this.patchEmailForm({ userMail: e.target.value }))}
            {tf(intl.formatMessage({ id: 'marketing.email.sender.password' }), f.password, e => this.patchEmailForm({ password: e.target.value }),
              { type: 'password', placeholder: this.state.emailFormId ? intl.formatMessage({ id: 'marketing.email.sender.passwordKeep' }) : '' })}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Box sx={{ flex: 1 }}>{tf(intl.formatMessage({ id: 'marketing.email.sender.dailyLimit' }), f.dailyLimit, e => this.patchEmailForm({ dailyLimit: e.target.value.replace(/\D/g, '') }), { type: 'number', helperText: intl.formatMessage({ id: 'marketing.email.sender.dailyLimitHint' }) })}</Box>
            </Box>
            {/* Testar envio: valida o SMTP antes de salvar. Senha em branco na edição usa a já gravada. */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
              <Box sx={{ flex: 1 }}>
                {tf(intl.formatMessage({ id: 'marketing.email.sender.testDest' }), this.state.emailTestDest, e => this.setState({ emailTestDest: e.target.value }), { type: 'email', placeholder: 'teste@exemplo.com' })}
              </Box>
              <Button variant="outlined" disabled={this.state.emailTesting} onClick={() => this.testEmailSend()}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap', mt: 0.25, borderColor: '#cfcfcf', color: '#444', flexShrink: 0 }}>
                {this.state.emailTesting ? <CircularProgress size={16} sx={{ color: '#8BC34A' }} /> : intl.formatMessage({ id: 'marketing.email.sender.test' })}
              </Button>
            </Box>
            <FormControlLabel control={<Switch checked={!!f.enableSsl} onChange={e => this.patchEmailForm({ enableSsl: e.target.checked })} />} label={intl.formatMessage({ id: 'marketing.email.sender.ssl' })} sx={{ '& .MuiFormControlLabel-label': { fontSize: 13 } }} />
            <FormControlLabel control={<Switch checked={!!f.isDefault} onChange={e => this.patchEmailForm({ isDefault: e.target.checked })} />} label={intl.formatMessage({ id: 'marketing.email.sender.setDefault' })} sx={{ '& .MuiFormControlLabel-label': { fontSize: 13 } }} />
            <FormControlLabel control={<Switch checked={!!f.active} onChange={e => this.patchEmailForm({ active: e.target.checked })} />} label={intl.formatMessage({ id: 'marketing.email.sender.activeField' })} sx={{ '& .MuiFormControlLabel-label': { fontSize: 13 } }} />
          </div>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={this.closeEmailModal} sx={{ textTransform: 'none', color: '#777' }}>{intl.formatMessage({ id: 'close' })}</Button>
          <Button variant="contained" disabled={this.state.emailFormSaving} onClick={() => this.saveEmailSender()}
            sx={{ textTransform: 'none', backgroundColor: '#8BC34A', '&:hover': { backgroundColor: '#7cb342' } }}>
            {this.state.emailFormSaving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : intl.formatMessage({ id: this.state.emailFormId ? 'marketing.email.sender.save' : 'marketing.email.sender.add' })}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  renderStepRecipients() {
    const { intl } = this.props;
    const list = this.selectedList();
    return (
      <Box sx={{ maxWidth: 620 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{intl.formatMessage({ id: 'marketing.field.list' })}</Typography>
          <Button size="small" sx={{ textTransform: 'none' }} onClick={() => this.props.history.push(`/${this.lang}/marketing/lists${this.originTab ? `?tab=${this.originTab}` : ''}`)}>
            {intl.formatMessage({ id: 'marketing.manage.lists' })}
          </Button>
        </Box>
        {this.acField(intl.formatMessage({ id: 'marketing.field.list' }), this.state.lists.map(l => ({ id: l.id, name: `${l.name} (${l.total})` })), this.state.listId, (id) => this.setState({ listId: id }, this.loadNoOptIn))}

        {list && (
          <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
            {/* Válidos: clicável → abre o preview de quem irá receber (paginado + download CSV) */}
            <Tooltip title={intl.formatMessage({ id: 'marketing.recipients.validTooltip' })} arrow>
              <Box onClick={this.openRecipientsPreview} sx={{ flex: 1, minWidth: 120, border: '1px solid #cfe8bf', borderRadius: '10px', p: 1.5, cursor: 'pointer', transition: 'all .15s', '&:hover': { backgroundColor: '#f3fae9', borderColor: '#8BC34A' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#5b9b1e' }}>{list.valid}</Typography>
                  <VisibilityOutlinedIcon sx={{ fontSize: 18, color: '#8BC34A' }} />
                </Box>
                <Typography sx={{ fontSize: 12, color: '#888' }}>{intl.formatMessage({ id: 'marketing.recipients.valid' })}</Typography>
              </Box>
            </Tooltip>
            <Box sx={{ flex: 1, minWidth: 120, border: '1px solid #e0e0e0', borderRadius: '10px', p: 1.5 }}>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#c0392b' }}>{list.invalid}</Typography>
              <Typography sx={{ fontSize: 12, color: '#888' }}>{intl.formatMessage({ id: 'marketing.recipients.invalid' })}</Typography>
            </Box>
            <Tooltip title={intl.formatMessage({ id: this.state.senderId ? 'marketing.recipients.noOptinTooltip' : 'marketing.recipients.noOptinPickSender' })} arrow>
              <Box sx={{ flex: 1, minWidth: 120, border: '1px solid #e0e0e0', borderRadius: '10px', p: 1.5 }}>
                <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#e67e22' }}>
                  {!this.state.senderId ? '—' : this.state.noOptinCount === null ? <CircularProgress size={18} sx={{ color: '#e67e22' }} /> : this.state.noOptinCount}
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#888' }}>{intl.formatMessage({ id: 'marketing.recipients.noOptin' })}</Typography>
              </Box>
            </Tooltip>
          </Box>
        )}
        {list && list.invalid > 0 && <Alert severity="info" sx={{ mt: 2 }}>{intl.formatMessage({ id: 'marketing.recipients.invalidNote' })}</Alert>}
        {this.renderRecipientsPreviewDialog()}
      </Box>
    );
  }

  // Modal: quem irá receber (contatos da lista, paginado no servidor) + botão Baixar .CSV.
  renderRecipientsPreviewDialog() {
    const { intl } = this.props;
    const list = this.selectedList();
    const total = list ? (list.total || 0) : 0;
    // Coluna de destino varia pelo meio: e-mail mostra e-mail; WhatsApp/SMS mostram telefone.
    const isEmail = this.state.deliveryMethod === 'email';
    const destColKey = isEmail ? 'marketing.col.email' : 'marketing.col.phone';
    return (
      <Dialog open={this.state.rcpPreviewOpen} onClose={this.closeRecipientsPreview} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px', maxHeight: '85vh' } }}>
        <DialogTitle sx={{ fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box>
            {intl.formatMessage({ id: 'marketing.recipients.preview.title' })}
            <Typography sx={{ fontSize: 12, color: '#888', fontWeight: 400 }}>{total} {intl.formatMessage({ id: 'marketing.list.preview.count' })}</Typography>
          </Box>
          <Button variant="outlined" size="small" startIcon={this.state.rcpDownloading ? <CircularProgress size={14} /> : <FileDownloadOutlinedIcon />}
            onClick={this.downloadRecipientsCsv} disabled={this.state.rcpDownloading}
            sx={{ textTransform: 'none', borderColor: '#cfcfcf', color: '#444', whiteSpace: 'nowrap' }}>
            {intl.formatMessage({ id: 'marketing.action.downloadCsv' })}
          </Button>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ maxHeight: '55vh', overflowY: 'auto', position: 'relative' }}>
            {this.state.rcpLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#8BC34A' }} /></Box>
            )}
            {!this.state.rcpLoading && (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ backgroundColor: '#fafafa', position: 'sticky', top: 0 }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, color: '#888', fontWeight: 600, backgroundColor: '#fafafa' }}>{intl.formatMessage({ id: 'marketing.list.col.name' })}</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 12, color: '#888', fontWeight: 600, backgroundColor: '#fafafa' }}>{intl.formatMessage({ id: destColKey })}</th>
                </tr></thead>
                <tbody>
                  {this.state.rcpItems.map((c, i) => (
                    <tr key={i}>
                      <td style={{ padding: '8px 12px', fontSize: 13, borderTop: '1px solid #f5f5f5' }}>{c.name}</td>
                      <td style={{ padding: '8px 12px', fontSize: 13, borderTop: '1px solid #f5f5f5', whiteSpace: 'nowrap' }}>{isEmail ? c.email : c.phone}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Box>
          <TablePagination component="div" count={total} page={this.state.rcpPage} rowsPerPage={this.state.rcpRpp}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={(_e, p) => this.changeRcpPage(p)}
            onRowsPerPageChange={(e) => this.changeRcpRpp(parseInt(e.target.value, 10))}
            labelRowsPerPage={intl.formatMessage({ id: 'marketing.list.preview.perPage' })}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`} />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={this.closeRecipientsPreview} sx={{ textTransform: 'none' }}>{intl.formatMessage({ id: 'close' })}</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Passo de conteúdo do SMS: texto livre + contador/segmentos + mapeamento das variáveis {{n}}.
  renderStepSms() {
    const { intl } = this.props;
    const body = this.state.messageBody || '';
    const len = body.length;
    const segments = this.smsSegments(len);
    const vars = this.smsVars();
    return (
      <Box sx={{ maxWidth: 720 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>{intl.formatMessage({ id: 'marketing.sms.body' })}</Typography>
        <TextField fullWidth multiline minRows={4} size="small" InputLabelProps={{ shrink: true }}
          placeholder={intl.formatMessage({ id: 'marketing.sms.bodyHelp' })}
          value={body} onChange={e => this.setState({ messageBody: e.target.value })} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          <Button size="small" variant="outlined" onClick={this.insertSmsVar} sx={{ textTransform: 'none', borderColor: '#cfcfcf', color: '#444' }}>
            {intl.formatMessage({ id: 'marketing.sms.insertVar' })}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: 12, color: '#888' }}>
            {len} {intl.formatMessage({ id: 'marketing.sms.chars' })} · {segments} {intl.formatMessage({ id: 'marketing.sms.segments' })}
          </Typography>
        </Box>

        {this.renderInvalidVarsAlert()}

        {vars.length > 0 && (
          <>
            <Typography sx={{ fontSize: 14, fontWeight: 600, mt: 2.5, mb: 1 }}>{intl.formatMessage({ id: 'marketing.template.variables' })}</Typography>
            {vars.map(v => {
              const m = this.state.varMapping[v] || { mode: 'field', field: '', fixed: '', fallback: '' };
              return (
                <Box key={v} sx={{ border: '1px solid #e8e8e8', borderRadius: '10px', p: 1.5, mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip size="small" label={v} sx={{ backgroundColor: '#eef7e3', color: '#5b9b1e', fontWeight: 700, borderRadius: '6px' }} />
                    <RadioGroup row value={m.mode} onChange={e => this.setVarMapping(v, { mode: e.target.value })}>
                      <FormControlLabel value="field" control={<Radio size="small" />} label={intl.formatMessage({ id: 'marketing.var.field' })} />
                      <FormControlLabel value="fixed" control={<Radio size="small" />} label={intl.formatMessage({ id: 'marketing.var.fixed' })} />
                    </RadioGroup>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {m.mode === 'field' ? (
                      <Box sx={{ minWidth: 200, flex: '0 1 220px' }}>
                        {this.acField(intl.formatMessage({ id: 'marketing.var.contactField' }), CONTACT_FIELDS, m.field, (id) => this.setVarMapping(v, { field: id }))}
                      </Box>
                    ) : (
                      <TextField size="small" InputLabelProps={{ shrink: true }} sx={{ minWidth: 220 }}
                        label={intl.formatMessage({ id: 'marketing.var.fixedValue' })} value={m.fixed} onChange={e => this.setVarMapping(v, { fixed: e.target.value })} />
                    )}
                    {m.mode === 'field' && (
                      <TextField size="small" InputLabelProps={{ shrink: true }} sx={{ minWidth: 220 }}
                        label={intl.formatMessage({ id: 'marketing.var.fallback' })} placeholder={intl.formatMessage({ id: 'marketing.var.fallbackPlaceholder' })}
                        value={m.fallback} onChange={e => this.setVarMapping(v, { fallback: e.target.value })} />
                    )}
                  </Box>
                </Box>
              );
            })}
          </>
        )}
      </Box>
    );
  }

  // Bloco de mapeamento de variáveis {{n}} (reusa a mesma UX do SMS; usado também no e-mail).
  renderVarMappingBlock(vars) {
    const { intl } = this.props;
    if (!vars || vars.length === 0) return null;
    return (
      <>
        <Typography sx={{ fontSize: 14, fontWeight: 600, mt: 2.5, mb: 1 }}>{intl.formatMessage({ id: 'marketing.template.variables' })}</Typography>
        {vars.map(v => {
          const m = this.state.varMapping[v] || { mode: 'field', field: '', fixed: '', fallback: '' };
          return (
            <Box key={v} sx={{ border: '1px solid #e8e8e8', borderRadius: '10px', p: 1.5, mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip size="small" label={v} sx={{ backgroundColor: '#eef7e3', color: '#5b9b1e', fontWeight: 700, borderRadius: '6px' }} />
                <RadioGroup row value={m.mode} onChange={e => this.setVarMapping(v, { mode: e.target.value })}>
                  <FormControlLabel value="field" control={<Radio size="small" />} label={intl.formatMessage({ id: 'marketing.var.field' })} />
                  <FormControlLabel value="fixed" control={<Radio size="small" />} label={intl.formatMessage({ id: 'marketing.var.fixed' })} />
                </RadioGroup>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {m.mode === 'field' ? (
                  <Box sx={{ minWidth: 200, flex: '0 1 220px' }}>
                    {this.acField(intl.formatMessage({ id: 'marketing.var.contactField' }), CONTACT_FIELDS, m.field, (id) => this.setVarMapping(v, { field: id }))}
                  </Box>
                ) : (
                  <TextField size="small" InputLabelProps={{ shrink: true }} sx={{ minWidth: 220 }}
                    label={intl.formatMessage({ id: 'marketing.var.fixedValue' })} value={m.fixed} onChange={e => this.setVarMapping(v, { fixed: e.target.value })} />
                )}
                {m.mode === 'field' && (
                  <TextField size="small" InputLabelProps={{ shrink: true }} sx={{ minWidth: 220 }}
                    label={intl.formatMessage({ id: 'marketing.var.fallback' })} placeholder={intl.formatMessage({ id: 'marketing.var.fallbackPlaceholder' })}
                    value={m.fallback} onChange={e => this.setVarMapping(v, { fallback: e.target.value })} />
                )}
              </Box>
            </Box>
          );
        })}
      </>
    );
  }

  renderAttachments() {
    const { intl } = this.props;
    const fmtSize = (n) => !n ? '' : (n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1048576).toFixed(1)} MB`);
    return (
      <Box sx={{ mt: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{intl.formatMessage({ id: 'marketing.email.attachments' })}</Typography>
          <Button component="label" size="small" variant="outlined" startIcon={<AttachFileIcon fontSize="small" />}
            disabled={this.state.uploadingAttachment} sx={{ textTransform: 'none', borderColor: '#cfcfcf', color: '#444' }}>
            {this.state.uploadingAttachment ? intl.formatMessage({ id: 'marketing.email.uploading' }) : intl.formatMessage({ id: 'marketing.email.addAttachment' })}
            <input hidden type="file" onChange={e => { const f = e.target.files && e.target.files[0]; e.target.value = ''; this.addAttachment(f); }} />
          </Button>
        </Box>
        {this.state.attachments.length === 0
          ? <Typography sx={{ fontSize: 12, color: '#999' }}>{intl.formatMessage({ id: 'marketing.email.noAttachments' })}</Typography>
          : this.state.attachments.map((a, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid #eee', borderRadius: '8px', px: 1.2, py: 0.6, mb: 0.75 }}>
              <AttachFileIcon sx={{ fontSize: 16, color: '#8BC34A' }} />
              <Typography sx={{ fontSize: 13, color: '#333', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</Typography>
              {a.size ? <Typography sx={{ fontSize: 11, color: '#999' }}>{fmtSize(a.size)}</Typography> : null}
              <IconButton size="small" onClick={() => this.removeAttachment(i)}><DeleteOutlineIcon sx={{ fontSize: 18, color: '#b5453f' }} /></IconButton>
            </Box>
          ))}
      </Box>
    );
  }

  // Passo de conteúdo do E-MAIL: compor (WYSIWYG) OU usar template salvo + assunto + variáveis + anexos.
  renderStepEmail() {
    const { intl } = this.props;
    const isTemplate = this.state.emailMode === 'template';
    const vars = this.emailVars();
    return (
      <Box sx={{ maxWidth: 760 }}>
        {/* Sub-modo: compor na hora OU usar template salvo */}
        <ToggleButtonGroup exclusive size="small" value={this.state.emailMode}
          onChange={(e, v) => { if (v) this.setState({ emailMode: v }); }}
          sx={{ mb: 2, '& .MuiToggleButton-root': { textTransform: 'none', px: 2, borderRadius: '10px !important', border: '1px solid #e0e0e0 !important', mr: 1 }, '& .Mui-selected': { backgroundColor: '#eef7e3 !important', color: '#5b9b1e', borderColor: '#8BC34A !important' } }}>
          <ToggleButton value="custom">{intl.formatMessage({ id: 'marketing.email.mode.compose' })}</ToggleButton>
          <ToggleButton value="template">{intl.formatMessage({ id: 'marketing.email.mode.template' })}</ToggleButton>
        </ToggleButtonGroup>

        {isTemplate && (
          <Box sx={{ mb: 2 }}>
            {this.acField(intl.formatMessage({ id: 'marketing.email.pickTemplate' }), this.state.emailTemplates, this.state.emailTemplateId, (id) => this.onPickEmailTemplate(id))}
          </Box>
        )}

        {/* Assunto (com inserir variável) */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', mb: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <TextField fullWidth size="small" InputLabelProps={{ shrink: true }}
              label={intl.formatMessage({ id: 'marketing.email.subject' })}
              value={this.state.emailSubject} onChange={e => this.setState({ emailSubject: e.target.value })} />
          </Box>
          <Button size="small" variant="outlined" onClick={() => this.insertEmailVar('subject')} sx={{ textTransform: 'none', borderColor: '#cfcfcf', color: '#444', whiteSpace: 'nowrap' }}>
            {intl.formatMessage({ id: 'marketing.email.insertVarSubject' })}
          </Button>
        </Box>

        {/* Corpo = preview + editor na MESMA peça (WYSIWYG Jodit em iframe isolado). No modo template o
            Jodit edita o DOCUMENTO completo (doctype/head/<style>/media queries) → renderiza com o CSS
            do template intacto, sem achatar (mesmo padrão da Automação). O template é só a semente
            (snapshot editável): o usuário troca variáveis de chamado ({{nome}}) por numéricas ({{1}}).
            Switch "HTML avançado" abre o Monaco pro HTML cru. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{intl.formatMessage({ id: 'marketing.email.body' })}</Typography>
          <Box sx={{ flex: 1 }} />
          {!this.state.emailAdvanced && (
            <ToggleButtonGroup exclusive size="small" value={this.state.emailDevice}
              onChange={(e, v) => { if (v) this.setState({ emailDevice: v }); }}
              sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.25, py: 0.25, fontSize: 12, borderRadius: '8px !important', border: '1px solid #e0e0e0 !important', mr: 0.5 }, '& .Mui-selected': { backgroundColor: '#eef7e3 !important', color: '#5b9b1e' } }}>
              <ToggleButton value="desktop">{intl.formatMessage({ id: 'automation.email.device.desktop' })}</ToggleButton>
              <ToggleButton value="mobile">{intl.formatMessage({ id: 'automation.email.device.mobile' })}</ToggleButton>
            </ToggleButtonGroup>
          )}
          <FormControlLabel sx={{ ml: 0.5, mr: 0 }}
            control={<Switch size="small" checked={this.state.emailAdvanced} onChange={e => this.setState({ emailAdvanced: e.target.checked })} />}
            label={<Typography sx={{ fontSize: 12, color: '#555' }}>{intl.formatMessage({ id: 'automation.email.advancedHtml' })}</Typography>} />
        </Box>
        {isTemplate && !this.state.emailBodyHtml && (
          <Typography sx={{ fontSize: 13, color: '#999', mb: 1 }}>{intl.formatMessage({ id: 'marketing.email.pickTemplateHint' })}</Typography>
        )}
        <Suspense fallback={<Skeleton variant="rounded" height={400} animation="wave" />}>
          {this.state.emailAdvanced ? (
            <CodeEditor ref={this.emailCodeRef} language="html" height={400}
              value={this.state.emailBodyHtml} onChange={(html) => this.setState({ emailBodyHtml: html })} />
          ) : (
            <Box sx={{ width: this.state.emailDevice === 'mobile' ? 390 : '100%', maxWidth: '100%', mx: 'auto', transition: 'width .2s ease', '& .jodit-container': { borderRadius: '12px', borderColor: '#e0e0e0' } }}>
              <EmailAssetRichEditor key={`${this.state.emailMode}-${this.state.emailDevice}`} ref={this.emailVisualRef}
                value={this.state.emailBodyHtml} onChange={(html) => this.setState({ emailBodyHtml: html })}
                fullDocument={isTemplate} height={400} locale={intl.locale} />
            </Box>
          )}
        </Suspense>
        <Box sx={{ mt: 1 }}>
          <Button size="small" variant="outlined" onClick={() => this.insertEmailVar('body')} sx={{ textTransform: 'none', borderColor: '#cfcfcf', color: '#444' }}>
            {intl.formatMessage({ id: 'marketing.email.insertVarBody' })}
          </Button>
        </Box>

        {this.renderInvalidVarsAlert()}
        {this.renderAttachments()}
        {this.renderVarMappingBlock(vars)}
      </Box>
    );
  }

  renderStepTemplate() {
    const { intl } = this.props;
    const tpl = this.selectedTemplate();
    return (
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Box sx={{ flex: '1 1 460px', minWidth: 0, maxWidth: 720 }}>
        {/* Atalho pra gerenciar modelos (templates), igual ao "Gerenciar listas" do passo 2 */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{intl.formatMessage({ id: 'marketing.field.template' })}</Typography>
          <Button size="small" startIcon={<DescriptionOutlinedIcon fontSize="small" />} sx={{ textTransform: 'none' }}
            onClick={() => this.props.history.push(`/${this.lang}/template-message`)}>
            {intl.formatMessage({ id: 'marketing.manage.templates' })}
          </Button>
        </Box>
        {this.acField(intl.formatMessage({ id: 'marketing.field.template' }), this.state.templates.map(t => ({ id: t.id, name: `${t.name}${t.category ? ` · ${t.category}` : ''}` })), this.state.templateId,
          (id) => { const t = this.state.templates.find(x => x.id === id); this.setState({ templateId: id, varMapping: {}, language: t ? t.language : '' }); }, { loading: this.state.loadingTemplates })}

        {/* Sem template aprovado pro número: avisa e leva pra gestão de modelos */}
        {this.state.senderId && !this.state.loadingTemplates && this.state.templates.length === 0 && (
          <Alert severity="warning" sx={{ mt: 1 }} action={
            <Button color="inherit" size="small" sx={{ textTransform: 'none' }} onClick={() => this.props.history.push(`/${this.lang}/template-message`)}>
              {intl.formatMessage({ id: 'marketing.manage.templates' })}
            </Button>
          }>{intl.formatMessage({ id: 'marketing.field.noApprovedTemplate' })}</Alert>
        )}

        {tpl && (
          <>
            {/* Categoria + idioma vêm do próprio template aprovado (não editável aqui). */}
            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              {tpl.category && (
                <Chip size="small" label={`${intl.formatMessage({ id: 'marketing.template.category' })}: ${tpl.category}`}
                  sx={{ borderRadius: '6px', fontWeight: 700, backgroundColor: '#f4ecfa', color: '#8e44ad', border: '1px solid #e0c8f0' }} />
              )}
              {tpl.language && (
                <Chip size="small" label={tpl.language}
                  sx={{ borderRadius: '6px', fontWeight: 600, backgroundColor: '#eef2f7', color: '#475569', border: '1px solid #dbe2ea' }} />
              )}
            </Box>

            {/* Pré-visualização do template (header/body/footer) — corpo agora carregado do template aprovado. */}
            <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#e9fde3', border: '1px solid #cfe8bf', borderRadius: '10px' }}>
              <Typography sx={{ fontSize: 11, color: '#5b9b1e', fontWeight: 600, mb: 0.5 }}>{intl.formatMessage({ id: 'marketing.template.body' })}</Typography>
              {tpl.header && <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a', whiteSpace: 'pre-wrap', mb: 0.5 }}>{tpl.header}</Typography>}
              <Typography sx={{ fontSize: 14, color: '#333', whiteSpace: 'pre-wrap' }}>{tpl.body}</Typography>
              {tpl.footer && <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.5, fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>{tpl.footer}</Typography>}
            </Box>

            <Alert severity="info" sx={{ mt: 1.5 }}>{intl.formatMessage({ id: 'marketing.template.window24h' })}</Alert>

            <Typography sx={{ fontSize: 14, fontWeight: 600, mt: 2.5, mb: 1 }}>{intl.formatMessage({ id: 'marketing.template.variables' })}</Typography>
            {tpl.vars.map(v => {
              const m = this.state.varMapping[v] || { mode: 'field', field: '', fixed: '', fallback: '' };
              return (
                <Box key={v} sx={{ border: '1px solid #e8e8e8', borderRadius: '10px', p: 1.5, mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip size="small" label={v} sx={{ backgroundColor: '#eef7e3', color: '#5b9b1e', fontWeight: 700, borderRadius: '6px' }} />
                    <RadioGroup row value={m.mode} onChange={e => this.setVarMapping(v, { mode: e.target.value })}>
                      <FormControlLabel value="field" control={<Radio size="small" />} label={intl.formatMessage({ id: 'marketing.var.field' })} />
                      <FormControlLabel value="fixed" control={<Radio size="small" />} label={intl.formatMessage({ id: 'marketing.var.fixed' })} />
                    </RadioGroup>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {m.mode === 'field' ? (
                      <Box sx={{ minWidth: 200, flex: '0 1 220px' }}>
                        {this.acField(intl.formatMessage({ id: 'marketing.var.contactField' }), CONTACT_FIELDS, m.field, (id) => this.setVarMapping(v, { field: id }))}
                      </Box>
                    ) : (
                      <TextField size="small" InputLabelProps={{ shrink: true }} sx={{ minWidth: 220 }}
                        label={intl.formatMessage({ id: 'marketing.var.fixedValue' })} value={m.fixed} onChange={e => this.setVarMapping(v, { fixed: e.target.value })} />
                    )}
                    {m.mode === 'field' && (
                      <TextField size="small" InputLabelProps={{ shrink: true }} sx={{ minWidth: 220 }}
                        label={intl.formatMessage({ id: 'marketing.var.fallback' })} placeholder={intl.formatMessage({ id: 'marketing.var.fallbackPlaceholder' })}
                        value={m.fallback} onChange={e => this.setVarMapping(v, { fallback: e.target.value })} />
                    )}
                  </Box>
                </Box>
              );
            })}
          </>
        )}
        </Box>

        {/* Coluna direita: Template de Opt-in */}
        <Box sx={{ flex: '1 1 320px', minWidth: 280, maxWidth: 400 }}>
          {this.renderOptinBlock()}
        </Box>
      </Box>
    );
  }

  // Bloco "Template de Opt-in" (coluna direita do passo 3). Pré-seleciona o padrão do número;
  // o usuário pode trocar e o preview atualiza. Informa que o opt-in é enviado se o destinatário ainda não optou.
  renderOptinBlock() {
    const { intl } = this.props;
    const opt = this.state.optinTemplates.find(t => t.id === this.state.optinTemplateId);
    return (
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '12px', p: 2, backgroundColor: '#fafbff' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <HowToRegOutlinedIcon sx={{ fontSize: 18, color: '#277afe' }} />
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#333', flex: 1 }}>{intl.formatMessage({ id: 'marketing.optin.title' })}</Typography>
          <Tooltip title={intl.formatMessage({ id: 'marketing.optin.refresh' })}>
            <span>
              <IconButton size="small" onClick={() => this.loadOptinTemplates(this.state.senderId, true)} disabled={!this.state.senderId || this.state.loadingOptin}
                sx={{ color: '#8BC34A', '&:hover': { backgroundColor: '#f1f8e9' } }}>
                {this.state.loadingOptin ? <CircularProgress size={16} sx={{ color: '#8BC34A' }} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Typography sx={{ fontSize: 12, color: '#888', mb: 1.5 }}>{intl.formatMessage({ id: 'marketing.optin.subtitle' })}</Typography>

        {!this.state.senderId ? (
          <Alert severity="info">{intl.formatMessage({ id: 'marketing.optin.pickSenderFirst' })}</Alert>
        ) : this.state.optinTemplates.length === 0 && !this.state.loadingOptin ? (
          <Alert severity="warning">{intl.formatMessage({ id: 'marketing.optin.none' })}</Alert>
        ) : (
          <>
            {this.acField(intl.formatMessage({ id: 'marketing.optin.template' }),
              this.state.optinTemplates.map(t => ({ id: t.id, name: `${t.name}${t.system ? ` · ${intl.formatMessage({ id: 'marketing.optin.default' })}` : ''}` })),
              this.state.optinTemplateId,
              (id) => this.setState({ optinTemplateId: id }),
              { loading: this.state.loadingOptin })}

            {opt && (
              <Box sx={{ mt: 1.5, p: 1.5, backgroundColor: '#e9fde3', border: '1px solid #cfe8bf', borderRadius: '10px' }}>
                <Typography sx={{ fontSize: 11, color: '#5b9b1e', fontWeight: 600, mb: 0.5 }}>{intl.formatMessage({ id: 'marketing.optin.preview' })}</Typography>
                {opt.header && <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a', whiteSpace: 'pre-wrap', mb: 0.5 }}>{opt.header}</Typography>}
                <Typography sx={{ fontSize: 14, color: '#333', whiteSpace: 'pre-wrap' }}>{opt.body}</Typography>
                {opt.footer && <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.5, fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>{opt.footer}</Typography>}
              </Box>
            )}

            <Alert severity="info" sx={{ mt: 1.5 }}>{intl.formatMessage({ id: 'marketing.optin.note' })}</Alert>
          </>
        )}
      </Box>
    );
  }

  renderStepReview() {
    const { intl } = this.props;
    const isSms = this.state.deliveryMethod === 'sms';
    const isEmail = this.state.deliveryMethod === 'email';
    const tpl = this.selectedTemplate();
    const optin = this.state.optinTemplates.find(t => t.id === this.state.optinTemplateId);
    return (
      <Box>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* Esquerda: pré-visualização (exemplos lado a lado) */}
          <Box sx={{ flex: '1 1 560px', minWidth: 0 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>{intl.formatMessage({ id: 'marketing.review.preview' })}</Typography>
            <Typography sx={{ fontSize: 12, color: '#888', mb: 1.5 }}>{intl.formatMessage({ id: 'marketing.review.previewNote' })}</Typography>
            {/* SMS: prévia em balões neutros (sem opt-in/idioma). */}
            {isSms && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                {SAMPLE_CONTACTS.slice(0, 2).map((c, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flex: '1 1 240px', minWidth: 230 }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: '#e8eaf6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <SmsOutlinedIcon sx={{ fontSize: 16, color: '#3f51b5' }} />
                    </Box>
                    <Box sx={{ backgroundColor: '#eef0f8', borderRadius: '10px', p: 1.2, flex: 1 }}>
                      <Typography sx={{ fontSize: 11, color: '#888', mb: 0.3 }}>{c.name || intl.formatMessage({ id: 'marketing.review.noName' })}{c.phone ? ` · ${c.phone}` : ''}</Typography>
                      <Typography sx={{ fontSize: 13, color: '#222', whiteSpace: 'pre-wrap' }}>{this.renderPreviewSms(c)}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
            {/* E-mail: prévia com assunto + corpo HTML (variáveis resolvidas) + anexos. */}
            {isEmail && (
              <Box>
                <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '12px', overflow: 'hidden', maxWidth: 560 }}>
                  <Box sx={{ backgroundColor: '#fafbff', px: 2, py: 1.2, borderBottom: '1px solid #eee' }}>
                    <Typography sx={{ fontSize: 11, color: '#888' }}>{intl.formatMessage({ id: 'marketing.email.subject' })}</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#222' }}>{this.resolveEmailText(this.state.emailSubject, SAMPLE_CONTACTS[0])}</Typography>
                  </Box>
                  {/* Preview em IFRAME isolado: renderiza o HTML do template com o CSS próprio (<style>/
                      media queries) e impede que esse CSS vaze pro resto da aplicação — o que o <div>
                      cru com dangerouslySetInnerHTML causava (layout quebrado + estilos vazando). */}
                  <Box sx={{ backgroundColor: '#fff' }}>
                    <iframe title="email-preview" sandbox="allow-same-origin"
                      srcDoc={this.resolveEmailText(this.state.emailBodyHtml, SAMPLE_CONTACTS[0])}
                      style={{ width: '100%', height: 360, border: 'none', display: 'block' }} />
                  </Box>
                </Box>
                {this.state.attachments.length > 0 && (
                  <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {this.state.attachments.map((a, i) => (
                      <Chip key={i} size="small" icon={<AttachFileIcon sx={{ fontSize: 14 }} />} label={a.name} sx={{ borderRadius: '6px' }} />
                    ))}
                  </Box>
                )}
              </Box>
            )}
            {!isSms && !isEmail && tpl && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                {/* 1º exemplo: mensagem de opt-in (enviada antes, se o destinatário ainda não optou) */}
                {optin && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flex: '1 1 240px', minWidth: 230 }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: '#e3edff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <HowToRegOutlinedIcon sx={{ fontSize: 16, color: '#277afe' }} />
                    </Box>
                    <Box sx={{ backgroundColor: '#eef4ff', border: '1px solid #d7e4ff', borderRadius: '10px', p: 1.2, flex: 1 }}>
                      <Typography sx={{ fontSize: 11, color: '#277afe', fontWeight: 700, mb: 0.3 }}>{intl.formatMessage({ id: 'marketing.review.optinExample' })}</Typography>
                      {optin.header && <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a', whiteSpace: 'pre-wrap', mb: 0.3 }}>{optin.header}</Typography>}
                      <Typography sx={{ fontSize: 13, color: '#222', whiteSpace: 'pre-wrap' }}>{optin.body}</Typography>
                      {optin.footer && <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.3, fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>{optin.footer}</Typography>}
                    </Box>
                  </Box>
                )}
                {/* Demais: 2 exemplos da mensagem do template selecionado */}
                {SAMPLE_CONTACTS.slice(0, 2).map((c, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flex: '1 1 240px', minWidth: 230 }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: '#dcf8c6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <WhatsAppIcon sx={{ fontSize: 16, color: '#25D366' }} />
                    </Box>
                    <Box sx={{ backgroundColor: '#dcf8c6', borderRadius: '10px', p: 1.2, flex: 1 }}>
                      <Typography sx={{ fontSize: 11, color: '#888', mb: 0.3 }}>{c.name || intl.formatMessage({ id: 'marketing.review.noName' })}{c.phone ? ` · ${c.phone}` : ''}</Typography>
                      <Typography sx={{ fontSize: 13, color: '#222' }}>{this.renderPreview(c)}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Direita: envio de teste. Título/nota com as MESMAS margens da esquerda, então
              o card de teste alinha no topo com os balões de exemplo. */}
          <Box sx={{ flex: '0 1 320px', minWidth: 280 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>{intl.formatMessage({ id: 'marketing.review.test' })}</Typography>
            <Typography sx={{ fontSize: 12, color: '#888', mb: 1.5 }}>{intl.formatMessage({ id: 'marketing.review.testNote' })}</Typography>
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '12px', p: 2, backgroundColor: '#fafbff' }}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'stretch', flexDirection: 'column' }}>
                {isEmail ? (
                  this.field({
                    type: 'email',
                    label: intl.formatMessage({ id: 'marketing.test.email' }),
                    value: this.state.testEmail,
                    onChange: (e) => this.setState({ testEmail: e.target.value }),
                  })
                ) : (
                  <PhoneInput
                    country="br"
                    value={this.state.testPhone}
                    onChange={(value) => this.setState({ testPhone: value })}
                    countryCodeEditable
                    enableSearch
                    disableSearchIcon
                    inputStyle={{ height: 42, width: '100%', fontSize: 14, paddingLeft: 52 }}
                    buttonStyle={{ height: 42, borderTopLeftRadius: 4, borderBottomLeftRadius: 4 }}
                    dropdownStyle={{ width: 260 }}
                    specialLabel=""
                  />
                )}
                <Button variant="outlined" onClick={this.sendTest}
                  startIcon={isEmail ? <EmailOutlinedIcon /> : (this.state.deliveryMethod === 'sms' ? <SmsOutlinedIcon /> : <WhatsAppIcon />)}
                  disabled={isEmail
                    ? !/^\S+@\S+\.\S+$/.test((this.state.testEmail || '').trim())
                    : (this.state.testPhone || '').replace(/\D/g, '').length < 12}
                  sx={{ textTransform: 'none' }}>{intl.formatMessage({ id: 'marketing.review.sendTest' })}</Button>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Custo estimado — largura total (estica até o fim, responsivo) */}
        <Divider sx={{ my: 2 }} />
        {this.renderCostEstimate()}
      </Box>
    );
  }

  renderCostEstimate() {
    const { intl } = this.props;
    const c = this.costEstimate();

    // E-mail: NÃO tem custo por unidade (caminho A). Mostra consumo/cota, não "custo".
    if (c.email) {
      const opt = this.selectedEmailOption();
      const isMexx = !opt || opt.isMexxDefault;
      const limit = (opt && opt.dailyLimit != null && !isNaN(Number(opt.dailyLimit)) && Number(opt.dailyLimit) > 0) ? Number(opt.dailyLimit) : null;
      const senderName = opt ? this.emailOptionLabel(opt) : intl.formatMessage({ id: 'marketing.email.sender.mexx' });
      const exceeds = limit != null && c.count > limit;
      return (
        <Box sx={{ p: 1.5, borderRadius: '10px', border: '1px solid #e0e0e0', backgroundColor: '#fafafa' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1, color: '#444' }}>{intl.formatMessage({ id: 'marketing.email.summary.title' })}</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography sx={{ fontSize: 13, color: '#555' }}>{intl.formatMessage({ id: 'marketing.cost.recipients' })}: <b>{c.count.toLocaleString('pt-BR')}</b></Typography>
            <Typography sx={{ fontSize: 13, color: '#555' }}>{intl.formatMessage({ id: 'marketing.email.sender.dispatchedBy' })}: <b style={{ color: '#555' }}>{senderName}</b></Typography>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontSize: 13, color: '#555' }}>
              {intl.formatMessage({ id: 'marketing.email.summary.limitLabel' })}: <b>{limit != null
                ? intl.formatMessage({ id: 'marketing.email.sender.limit' }, { n: limit.toLocaleString('pt-BR') })
                : intl.formatMessage({ id: 'marketing.email.sender.noLimit' })}</b>
            </Typography>
          </Box>
          {exceeds && (
            <Alert severity="warning" sx={{ mt: 1, py: 0, fontSize: 12, '& .MuiAlert-message': { fontSize: 12 } }}>
              {intl.formatMessage({ id: 'marketing.email.summary.exceeds' }, { r: c.count.toLocaleString('pt-BR'), n: limit.toLocaleString('pt-BR') })}
            </Alert>
          )}
          {isMexx && (
            <Typography sx={{ fontSize: 11, color: '#999', mt: 0.75 }}>
              {intl.formatMessage({ id: 'marketing.email.limit.advisory' }, { n: (limit || 0).toLocaleString('pt-BR') })}
            </Typography>
          )}
        </Box>
      );
    }

    const fmt = c.sms ? (v) => `R$ ${v.toFixed(2)}` : (v) => `US$ ${v.toFixed(4)}`;
    const showValue = !c.sms || c.priceKnown;
    return (
      <Box sx={{ p: 1.5, borderRadius: '10px', border: '1px solid #ffe0b2', backgroundColor: '#fff8ef' }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1, color: '#b9770e' }}>{intl.formatMessage({ id: 'marketing.cost.title' })}</Typography>
        {c.sms && c.senderName && (
          <Typography sx={{ fontSize: 12, color: '#777', mb: 0.75 }}>
            {intl.formatMessage({ id: 'marketing.cost.sms.sender' })}: <b style={{ color: '#555' }}>{c.senderName}</b>
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {c.sms && (<Typography sx={{ fontSize: 13, color: '#555' }}>{intl.formatMessage({ id: 'marketing.cost.sms.segments' }, { n: c.segments })}</Typography>)}
          {showValue && (<Typography sx={{ fontSize: 13, color: '#555' }}>{intl.formatMessage({ id: 'marketing.cost.unit' })}: <b>{fmt(c.unit)}</b></Typography>)}
          <Typography sx={{ fontSize: 13, color: '#555' }}>{intl.formatMessage({ id: 'marketing.cost.recipients' })}: <b>{c.count.toLocaleString('pt-BR')}</b></Typography>
          <Box sx={{ flex: 1 }} />
          {showValue
            ? (<Typography sx={{ fontSize: 16, fontWeight: 700, color: '#b9770e' }}>{intl.formatMessage({ id: 'marketing.cost.total' })}: {fmt(c.total)}</Typography>)
            : (<Typography sx={{ fontSize: 12, color: '#b9770e' }}>{intl.formatMessage({ id: 'marketing.cost.sms.noprice' })}</Typography>)}
        </Box>
        <Typography sx={{ fontSize: 11, color: '#999', mt: 0.5 }}>{intl.formatMessage({ id: 'marketing.cost.note' })}</Typography>
      </Box>
    );
  }

  modeCard(mode, titleId, descId, icon) {
    const { intl } = this.props;
    const active = this.state.schedule.mode === mode;
    return (
      <Box onClick={() => this.patchSchedule({ mode })} sx={{
        flex: 1, minWidth: 150, cursor: 'pointer', p: 1.5, borderRadius: '10px',
        border: active ? '2px solid #8BC34A' : '1px solid #e0e0e0',
        backgroundColor: active ? '#f3fae9' : '#fff', transition: 'all .2s',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {React.cloneElement(icon, { sx: { color: active ? '#5b9b1e' : '#9e9e9e', fontSize: 20 } })}
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: active ? '#5b9b1e' : '#444' }}>{intl.formatMessage({ id: titleId })}</Typography>
        </Box>
        <Typography sx={{ fontSize: 12, color: '#888', mt: 0.5 }}>{intl.formatMessage({ id: descId })}</Typography>
      </Box>
    );
  }

  freqButton(freq, labelId) {
    const { intl } = this.props;
    const active = this.state.schedule.recurrence.freq === freq;
    return (
      <Button onClick={() => this.patchRecurrence({ freq })} variant={active ? 'contained' : 'outlined'} size="small"
        sx={{ textTransform: 'none', backgroundColor: active ? '#8BC34A' : 'transparent', color: active ? '#fff' : '#555', borderColor: '#cfcfcf', '&:hover': { backgroundColor: active ? '#7cb342' : 'rgba(0,0,0,0.03)' } }}>
        {intl.formatMessage({ id: labelId })}
      </Button>
    );
  }

  renderRecurrenceBuilder() {
    const { intl } = this.props;
    const r = this.state.schedule.recurrence;
    const timeField = (
      <TextField type="time" size="small" InputLabelProps={{ shrink: true }} sx={{ minWidth: 130 }}
        label={intl.formatMessage({ id: 'marketing.schedule.time' })} value={r.time} onChange={e => this.patchRecurrence({ time: e.target.value })} />
    );
    const intervalField = (label) => (
      <TextField type="number" size="small" InputLabelProps={{ shrink: true }} sx={{ width: 130 }} inputProps={{ min: 1 }}
        label={label} value={r.interval} onChange={e => this.patchRecurrence({ interval: Math.max(1, Number(e.target.value)) })} />
    );
    return (
      <Box sx={{ mt: 1.5, p: 2, border: '1px solid #e8e8e8', borderRadius: '10px', backgroundColor: '#fafafa' }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          {this.freqButton('daily', 'marketing.schedule.freq.daily')}
          {this.freqButton('weekly', 'marketing.schedule.freq.weekly')}
          {this.freqButton('monthly', 'marketing.schedule.freq.monthly')}
        </Box>

        {r.freq === 'daily' && (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {intervalField(intl.formatMessage({ id: 'marketing.schedule.everyDays' }))}
            {timeField}
          </Box>
        )}

        {r.freq === 'weekly' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              {intervalField(intl.formatMessage({ id: 'marketing.schedule.everyWeeks' }))}
              {timeField}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.7 }}>
              {WEEKDAYS.map(d => {
                const on = r.weekdays.includes(d.id);
                return (
                  <Box key={d.id} onClick={() => this.toggleWeekday(d.id)} sx={{
                    width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                    fontSize: 13, fontWeight: 700, transition: 'all .15s',
                    backgroundColor: on ? '#8BC34A' : '#fff', color: on ? '#fff' : '#888', border: on ? '1px solid #8BC34A' : '1px solid #ddd',
                  }}>{d.label}</Box>
                );
              })}
            </Box>
          </Box>
        )}

        {r.freq === 'monthly' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <RadioGroup value={r.monthlyMode} onChange={e => this.patchRecurrence({ monthlyMode: e.target.value })}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <FormControlLabel value="dayOfMonth" control={<Radio size="small" />} label={intl.formatMessage({ id: 'marketing.schedule.onDay' })} />
                <TextField type="number" size="small" InputLabelProps={{ shrink: true }} sx={{ width: 90 }} inputProps={{ min: 1, max: 31 }}
                  disabled={r.monthlyMode !== 'dayOfMonth'} value={r.dayOfMonth} onChange={e => this.patchRecurrence({ dayOfMonth: Math.min(31, Math.max(1, Number(e.target.value))) })} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                <FormControlLabel value="nthWeekday" control={<Radio size="small" />} label={intl.formatMessage({ id: 'marketing.schedule.onThe' })} />
                <TextField select size="small" SelectProps={{ native: true }} sx={{ width: 130 }} disabled={r.monthlyMode !== 'nthWeekday'}
                  value={r.nth} onChange={e => this.patchRecurrence({ nth: Number(e.target.value) })}>
                  <option value={1}>{intl.formatMessage({ id: 'marketing.schedule.nth.1' })}</option>
                  <option value={2}>{intl.formatMessage({ id: 'marketing.schedule.nth.2' })}</option>
                  <option value={3}>{intl.formatMessage({ id: 'marketing.schedule.nth.3' })}</option>
                  <option value={4}>{intl.formatMessage({ id: 'marketing.schedule.nth.4' })}</option>
                  <option value={-1}>{intl.formatMessage({ id: 'marketing.schedule.nth.last' })}</option>
                </TextField>
                <TextField select size="small" SelectProps={{ native: true }} sx={{ width: 150 }} disabled={r.monthlyMode !== 'nthWeekday'}
                  value={r.nthWeekday} onChange={e => this.patchRecurrence({ nthWeekday: Number(e.target.value) })}>
                  {WEEKDAY_NAMES.map((n, i) => <option key={i} value={i}>{n}</option>)}
                </TextField>
              </Box>
            </RadioGroup>
            <Box>{timeField}</Box>
          </Box>
        )}

        {/* Condição de término (comum a todas as frequências): nunca | após N execuções | até a data */}
        <Divider sx={{ my: 1.5 }} />
        <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>{intl.formatMessage({ id: 'marketing.schedule.ends' })}</Typography>
        <RadioGroup value={r.endMode || 'never'} onChange={e => this.patchRecurrence({ endMode: e.target.value })}>
          <FormControlLabel value="never" control={<Radio size="small" />} label={intl.formatMessage({ id: 'marketing.schedule.ends.never' })} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <FormControlLabel value="count" control={<Radio size="small" />} label={intl.formatMessage({ id: 'marketing.schedule.ends.afterN' })} />
            <TextField type="number" size="small" sx={{ width: 90 }} inputProps={{ min: 1 }} disabled={r.endMode !== 'count'}
              value={r.endCount} onChange={e => this.patchRecurrence({ endCount: Math.max(1, Number(e.target.value)) })} />
            <Typography sx={{ fontSize: 13, color: '#666' }}>{intl.formatMessage({ id: 'marketing.schedule.ends.executions' })}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <FormControlLabel value="date" control={<Radio size="small" />} label={intl.formatMessage({ id: 'marketing.schedule.ends.onDate' })} />
            <TextField type="date" size="small" InputLabelProps={{ shrink: true }} sx={{ width: 180 }} disabled={r.endMode !== 'date'}
              value={r.endDate} onChange={e => this.patchRecurrence({ endDate: e.target.value })} />
          </Box>
        </RadioGroup>
      </Box>
    );
  }

  renderStepSchedule() {
    const { intl } = this.props;
    const s = this.state.schedule;
    return (
      <Box sx={{ maxWidth: 720 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
          {this.modeCard('now', 'marketing.schedule.now', 'marketing.schedule.now.desc', <BoltIcon />)}
          {this.modeCard('once', 'marketing.schedule.once', 'marketing.schedule.once.desc', <EventOutlinedIcon />)}
          {this.modeCard('recurring', 'marketing.schedule.recurring', 'marketing.schedule.recurring.desc', <RepeatIcon />)}
        </Box>

        {s.mode === 'once' && (
          <TextField type="datetime-local" size="small" InputLabelProps={{ shrink: true }} sx={{ mt: 1, minWidth: 260 }}
            label={intl.formatMessage({ id: 'marketing.schedule.datetime' })} value={s.onceAt} onChange={e => this.patchSchedule({ onceAt: e.target.value })} />
        )}

        {s.mode === 'recurring' && this.renderRecurrenceBuilder()}

        {s.mode !== 'now' && (
          <Box sx={{ mt: 2, maxWidth: 300 }}>
            {this.acField(intl.formatMessage({ id: 'marketing.schedule.timezone' }), TIMEZONES, s.timezone, (id) => this.patchSchedule({ timezone: id }))}
          </Box>
        )}

        {/* Resumo legível do agendamento */}
        <Box sx={{ mt: 2, p: 1.2, px: 1.5, borderRadius: '8px', backgroundColor: '#eef7e3', border: '1px solid #cfe8bf', display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventOutlinedIcon sx={{ fontSize: 18, color: '#5b9b1e' }} />
          <Typography sx={{ fontSize: 13, color: '#5b9b1e', fontWeight: 600 }}>{this.recurrenceSummary()}</Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Switches (bool on/off) */}
        <FormControlLabel sx={{ display: 'flex', m: 0, justifyContent: 'space-between' }} labelPlacement="start"
          control={<Switch checked={s.quietHours} onChange={() => this.patchSchedule({ quietHours: !s.quietHours })} sx={{ '& .Mui-checked': { color: '#8BC34A' }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: '#8BC34A !important' } }} />}
          label={<Typography sx={{ fontSize: 14 }}>{intl.formatMessage({ id: 'marketing.schedule.quiet' })}</Typography>} />
        <FormControlLabel sx={{ display: 'flex', m: 0, justifyContent: 'space-between', mt: 1 }} labelPlacement="start"
          control={<Switch checked={s.slice} onChange={() => this.patchSchedule({ slice: !s.slice })} sx={{ '& .Mui-checked': { color: '#8BC34A' }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: '#8BC34A !important' } }} />}
          label={<Typography sx={{ fontSize: 14 }}>{intl.formatMessage({ id: 'marketing.schedule.slice' })}</Typography>} />

        {s.slice && (
          <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap', alignItems: 'center', pl: 1 }}>
            <TextField type="number" size="small" InputLabelProps={{ shrink: true }} sx={{ width: 160 }} inputProps={{ min: 1 }}
              label={intl.formatMessage({ id: 'marketing.schedule.sliceBatch' })} value={s.sliceBatch} onChange={e => this.patchSchedule({ sliceBatch: Math.max(1, Number(e.target.value)) })} />
            <TextField type="number" size="small" InputLabelProps={{ shrink: true }} sx={{ width: 160 }} inputProps={{ min: 1 }}
              label={intl.formatMessage({ id: 'marketing.schedule.sliceInterval' })} value={s.sliceInterval} onChange={e => this.patchSchedule({ sliceInterval: Math.max(1, Number(e.target.value)) })} />
            <Typography sx={{ fontSize: 12, color: '#888' }}>
              {intl.formatMessage({ id: 'marketing.schedule.sliceSummary' }, { batch: s.sliceBatch, interval: s.sliceInterval })}
            </Typography>
          </Box>
        )}

        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mt: 2 }}>{intl.formatMessage({ id: 'marketing.schedule.costNote' })}</Alert>
      </Box>
    );
  }

  renderStepContent() {
    switch (this.state.activeStep) {
      case 0: return this.renderStepData();
      case 1: return this.renderStepRecipients();
      case 2: return this.state.deliveryMethod === 'sms' ? this.renderStepSms()
        : this.state.deliveryMethod === 'email' ? this.renderStepEmail()
        : this.renderStepTemplate();
      case 3: return this.renderStepReview();
      case 4: return this.renderStepSchedule();
      default: return null;
    }
  }

  // Skeleton da tela inteira (stepper + formulário) enquanto catálogos/detalhe carregam.
  renderLoadingSkeleton() {
    return (
      <React.Fragment>
        {/* trilha do stepper */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4, px: 2 }}>
          {[0, 1, 2, 3, 4].map(i => (
            <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, flex: 1 }}>
              <Skeleton variant="circular" width={32} height={32} animation="wave" />
              <Skeleton variant="text" width={64} height={14} animation="wave" />
            </Box>
          ))}
        </Box>
        {/* corpo do formulário */}
        <Box sx={{ minHeight: 280 }}>
          <Skeleton variant="text" width="30%" height={28} animation="wave" sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={56} animation="wave" sx={{ borderRadius: 1, mb: 2 }} />
          <Skeleton variant="rectangular" height={56} animation="wave" sx={{ borderRadius: 1, mb: 2 }} />
          <Skeleton variant="text" width="22%" height={22} animation="wave" sx={{ mb: 1 }} />
          <Skeleton variant="rectangular" height={120} animation="wave" sx={{ borderRadius: 1 }} />
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Skeleton variant="rectangular" width={100} height={36} animation="wave" sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width={140} height={36} animation="wave" sx={{ borderRadius: 1 }} />
        </Box>
      </React.Fragment>
    );
  }

  render() {
    const { classes, headerMenu, actionsHeaderMenu, intl } = this.props;
    const { activeStep, saving, notification, loading } = this.state;
    const isLast = activeStep === 4;

    return (
      <div className={classes.root} style={{ paddingLeft: 0, marginLeft: 0 }}>
        <HeaderMenu />
        <main className={clsx(classes.content, { [classes.contentShift]: headerMenu.open }, classes.actionsContent, { [classes.actionscontentShift]: actionsHeaderMenu.open })}>
          <div className={classes.drawerHeader} />
          <Container component='main' maxWidth='xl' style={{ maxWidth: '100%', paddingLeft: 0, marginLeft: 0 }}>
            <CssBaseline />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Button startIcon={<ArrowBackIcon />} onClick={this.cancel} sx={{ textTransform: 'none', color: '#666' }}>{intl.formatMessage({ id: 'marketing.back.list' })}</Button>
              <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#333' }}>{intl.formatMessage({ id: this.isEdit ? 'marketing.edit.broadcast' : 'marketing.new.broadcast' })}</Typography>
            </Box>

            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '12px', backgroundColor: '#fff', p: 3 }}>
              {loading ? this.renderLoadingSkeleton() : (<React.Fragment>
              <Stepper activeStep={activeStep} alternativeLabel sx={{
                mb: 4,
                // conector mais suave; preenche em verde até o step atual
                '& .MuiStepConnector-line': { borderColor: '#e6e6e6' },
                '& .MuiStep-root .Mui-active + .MuiStepConnector-root .MuiStepConnector-line': { borderColor: '#8BC34A' },
                '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line, & .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': { borderColor: '#8BC34A' },
                '& .MuiStepLabel-label': { color: '#9e9e9e', fontSize: 13, mt: 0.5 },
                '& .MuiStepLabel-label.Mui-active': { color: '#5b9b1e', fontWeight: 700 },
                '& .MuiStepLabel-label.Mui-completed': { color: '#5b9b1e', fontWeight: 600 },
              }}>
                {this.steps.map((s, i) => (
                  <Step key={s}>
                    <StepLabel StepIconComponent={StepCircle}
                      onClick={this.isEdit ? () => this.goToStep(i) : undefined}
                      sx={this.isEdit ? { cursor: 'pointer', '&, & *': { cursor: 'pointer !important' } } : undefined}>
                      {s}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Box sx={{ minHeight: 280 }}>{this.renderStepContent()}</Box>

              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={activeStep === 0 ? this.cancel : this.back} sx={{ textTransform: 'none' }}>
                  {activeStep === 0 ? intl.formatMessage({ id: 'cancel' }) : intl.formatMessage({ id: 'marketing.back' })}
                </Button>
                {!isLast ? (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {/* No passo de revisão, Salvar rascunho fica à esquerda do Avançar (mesmo lugar do passo final) */}
                    {activeStep === 3 && (
                      <Button variant="outlined" onClick={this.saveDraftInline} disabled={saving}
                        sx={{ textTransform: 'none', borderColor: '#cfcfcf', color: '#444' }}>
                        {intl.formatMessage({ id: 'marketing.create.saveDraft' })}
                      </Button>
                    )}
                    <Button variant="contained" onClick={this.next} disabled={!this.canNext()}
                      sx={{ textTransform: 'none', backgroundColor: '#8BC34A', '&:hover': { backgroundColor: '#7cb342' } }}>
                      {intl.formatMessage({ id: 'marketing.next' })}
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Editando uma programada já não é rascunho: oferece só "Salvar". Rascunho/criação mantêm o "Salvar rascunho". */}
                    {!(this.isEdit && this.state.loadedStatus === 'scheduled') && (
                      <Button variant="outlined" onClick={() => this.submit('draft')} disabled={saving}
                        sx={{ textTransform: 'none', borderColor: '#cfcfcf', color: '#444' }}>
                        {intl.formatMessage({ id: 'marketing.create.saveDraft' })}
                      </Button>
                    )}
                    {this.canPublish() && (
                      <Button variant="contained" onClick={() => this.submit(this.state.schedule.mode === 'now' ? 'active' : 'scheduled')} disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ textTransform: 'none', backgroundColor: '#8BC34A', '&:hover': { backgroundColor: '#7cb342' } }}>
                        {this.isEdit
                          ? intl.formatMessage({ id: 'marketing.create.save' })
                          : (this.state.schedule.mode === 'now' ? intl.formatMessage({ id: 'marketing.create.send' }) : intl.formatMessage({ id: 'marketing.create.schedule' }))}
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
              </React.Fragment>)}
            </Box>
          </Container>
          <Footer />
        </main>
        {saving && (
          <Overlay>
            <CircularProgress color="primary" />
          </Overlay>
        )}
        <CustomizedSnackbars variant={notification.variant} message={notification.message} isOpen={notification.open}
          toClose={() => this.setState({ notification: { ...notification, open: false } })} />
      </div>
    );
  }
}

const mapStateToProps = state => ({ headerMenu: state.headerMenu, actionsHeaderMenu: state.actionsHeaderMenu, userSession: state.userSession });
const mapDispatchToProps = dispatch => bindActionCreators(componentActions, dispatch);
export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(withStyles(Styles)(MarketingBroadcastNew)));
